import '../styles/responsive/pages/Invoices.css';
import '../styles/enterprise.css';
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { fetchWithAuth } from "../utils/api";
import { stripEmoji, hasHtmlTag, HTML_TAG_MESSAGE } from '../utils/sanitizeText';
import { formatUSD, formatCount, formatDuration } from "../utils/formatAmount";
import { deriveCounts } from "../utils/stats";
import useDebounce from "../hooks/useDebounce";
import StatusBadge from "../components/common/StatusBadge";
import AssetBadge from "../components/common/AssetBadge";
import EmptyState from "../components/common/EmptyState";
import { TableSkeleton } from "../components/common/Skeleton";
import PageHeaderPro from "../components/common/PageHeaderPro";
import StatCard from "../components/common/StatCard";
import FilterTabs from "../components/common/FilterTabs";
import Pager from "../components/common/Pager";
import EntSearch from "../components/common/EntSearch";
import {
  Plus,
  Eye,
  MoreVertical,
  FileText,
  Mail,
  X,
  Calendar,
  Copy,
  Trash2,
  Maximize2,
  Minimize2,
  Edit3,
  Clock,
  AlertTriangle,
  CheckCircle2,
  FileClock,
  Filter,
  Columns,
  Check,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Columns that carry the identity of an invoice — always rendered, never
// hideable from the Columns dropdown.
const LOCKED_COLUMNS = ["invoice", "customer", "amount", "status", "actions"];

// Customer name is capped so it stays on one line in the invoice table,
// the PDF header and the payment-page heading.
const CUSTOMER_NAME_MAX = 40;
const DESCRIPTION_MAX = 300; // matches createInvoiceValidation on the API
const LINE_ITEM_DESCRIPTION_MAX = 300; // matches lineItems.*.description on the API
const CLIENT_NOTE_MAX = 500; // matches metadata.clientNote on the API
const TERMS_MAX = 500; // matches metadata.termsConditions on the API

// Letters only, in any script, plus the separators real names contain. Mirrors
// NAME_PATTERN in the API's invoice validation, so the form and the server agree.
// \p{L} allows accented and non-Latin letters (José, Müller, 张) and excludes
// digits, punctuation and emoji, since emoji are symbols rather than letters.
const NAME_ALLOWED = /^[\p{L}\s'.-]*$/u;

// Strips anything the pattern would reject, so a pasted name is cleaned rather
// than silently failing validation on submit. Emoji are already excluded here:
// they are symbols, not \p{L} letters.
const sanitizeName = (value) => value.replace(/[^\p{L}\s'.-]/gu, '');

// The free-text fields below accept almost anything, so emoji need removing
// explicitly. See utils/sanitizeText for what counts as one and why © ® ™ are
// kept. Applied in onChange rather than on submit so a pasted emoji never
// appears in the field at all -- the invoice is a document that gets rendered
// to PDF and emailed, and emoji survive neither reliably.

// Local calendar date, not UTC. toISOString() returns the UTC day, which is
// the wrong day for part of every local day: east of UTC it reports
// yesterday until mid-morning, west of it tomorrow from the evening. As the
// `min` of a due-date field that meant a past date was sometimes selectable
// and today sometimes was not.
const toDateInput = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const getTodayDate = () => toDateInput(new Date());

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return toDateInput(tomorrow);
};

// The due-date fields are picker-only. `min` alone governs the calendar and
// the browser's own validity flag, but a past date can still be typed into
// the segments and submitted; blocking the keys closes that off.
//
// Enter and Space open the calendar so the field is still reachable without
// a mouse. showPicker needs a user gesture and throws without one, hence the
// catch.
const openDatePicker = (e) => {
  const el = e.currentTarget;
  try {
    el.showPicker?.();
  } catch {
    /* no user gesture — the click handler will get it */
  }
};

const pickerOnlyProps = {
  onKeyDown: (e) => {
    // Let navigation and the browser's own shortcuts through.
    if (e.key === "Tab" || e.key === "Escape" || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    if (e.key === "Enter" || e.key === " ") openDatePicker(e);
  },
  onPaste: (e) => e.preventDefault(),
  onDrop: (e) => e.preventDefault(),
  onClick: openDatePicker,
};

// Belt and braces: a value from autofill or a restored form never lands in
// state if it is in the past.
const acceptDueDate = (value) => !value || value >= getTodayDate();

const Invoices = () => {
  const { mode } = useTheme();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => {
    const validStatuses = [
      "open",
      "pending",
      "paid",
      "overpaid",
      "partially_paid",
      "overdue",
      "cancelled",
      "draft",
    ];
    const statusParam = searchParams.get("status");
    return statusParam && validStatuses.includes(statusParam)
      ? statusParam
      : "all";
  });
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [invoiceForm, setInvoiceForm] = useState({
    customerName: "",
    customerEmail: "",
    amount: "",
    dueDate: "",
    description: "",
    memo: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [previewMode, setPreviewMode] = useState("Payment");
  const [showPreview, setShowPreview] = useState(false);
  const [isDrawerMaximized, setIsDrawerMaximized] = useState(false);

  const [lineItems, setLineItems] = useState([
    { id: 1, description: "", quantity: 1, unitPrice: "", amount: 0 },
  ]);

  const [gstValue, setGstValue] = useState("");
  const [includeGst, setIncludeGst] = useState(false);
  const [gstType, setGstType] = useState("percentage"); 

  const [clientNote, setClientNote] = useState("");
  const [termsConditions, setTermsConditions] = useState("");

  const [invoices, setInvoices] = useState([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [invoicesError, setInvoicesError] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [invoiceStats, setInvoiceStats] = useState({
    totalInvoices: 0,
    paidInvoices: 0,
    fullyPaidInvoices: 0,
    pendingInvoices: 0,
    overdueInvoices: 0,
    overpaidInvoices: 0,
    unsupportedInvoices: 0,
    partiallyPaidInvoices: 0,
    totalPaid: 0,
    totalRemaining: 0,
    totalInvoiceAmount: 0,
    totalOverdueAmount: 0,
    // null until the backend reports a settled sample to average over.
    avgPaymentSeconds: null,
    avgPaymentSampleSize: 0,
  });

  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [statsError, setStatsError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [freshTick, setFreshTick] = useState(0);

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDueDateModal, setShowDueDateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [newDueDate, setNewDueDate] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const [merchants, setMerchants] = useState([]);
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [isMerchantsLoading, setIsMerchantsLoading] = useState(false);

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    invoice: true,
    amount: true,
    asset: true,
    customer: true,
    status: true,
    dueDate: true,
    created: true,
    merchant: true,
    actions: true,
  });



  //
  const lockedColumns = ["invoice", "amount"];



  const generateUniqueInvoiceId = () => {
    const randomNum = Math.floor(100000 + Math.random() * 900000); 
    return `INV-${randomNum}`;
  };
  const [nextInvoiceId, setNextInvoiceId] = useState(generateUniqueInvoiceId);

  // Only the latest request of each kind may write state. ThemeContext starts
  // every page load in 'test' and switches to the saved mode a moment later, so
  // two requests (test, then live) are always in flight together and can land
  // in either order: the KPI cards read $0 or the real figure depending on
  // which reply arrived last. Filter and page clicks race the same way.
  const invoicesSeq = useRef(0);
  const statsSeq = useRef(0);

  const fetchInvoices = useCallback(async () => {
    const seq = ++invoicesSeq.current;
    setIsLoadingInvoices(true);
    setInvoicesError(false);
    try {
      const token = localStorage.getItem("token");

      const params = new URLSearchParams({
        sortBy,
        sortOrder,
        page: currentPage,
        limit: pageSize,
      });

      params.append('isTestnet', mode === 'test' ? 'true' : 'false');

      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      if (debouncedSearchTerm) {
        params.append("search", debouncedSearchTerm);
      }

      if (isSuperAdmin && merchantFilter && merchantFilter !== "all") {
        params.append("merchantId", merchantFilter);
      }

      const response = await fetchWithAuth(
        `${API_BASE_URL}/invoices?${params.toString()}`
      );
      const data = await response.json();
      if (seq !== invoicesSeq.current) return;

      if (data.success && data.data) {
        const mappedInvoices = data.data.invoices.map((inv) => ({
          id: inv.invoiceId,
          customer_name: inv.customerName,
          customer_email: inv.customerEmail,
          amount: parseFloat(inv.amount),
          currency: inv.token || inv.currency || "USD",
          // Asset actually selected for payment. Null until a payment session
          // picks one, so an unpaid invoice legitimately has no asset yet.
          token: inv.token || null,
          network: inv.network || null,
          status: inv.status,
          due_date: inv.dueDate,
          created_at: inv.createdAt,
          merchantId: inv.merchantId,
          merchant: inv.merchant,
          linkId: inv.linkId,
        }));
        setInvoices(mappedInvoices);

        if (data.data.pagination) {
          setTotalPages(data.data.pagination.totalPages);
          setTotalItems(data.data.pagination.total);
        }
        setLastUpdated(Date.now());
      }
    } catch (error) {
      if (seq !== invoicesSeq.current) return;
      console.error("Failed to fetch invoices:", error);
      setInvoicesError(true);
    } finally {
      if (seq === invoicesSeq.current) setIsLoadingInvoices(false);
    }
  }, [sortBy, sortOrder, currentPage, pageSize, statusFilter, debouncedSearchTerm, mode, isSuperAdmin, merchantFilter]);

  const fetchInvoiceStats = useCallback(async () => {
    const seq = ++statsSeq.current;
    setStatsError(false);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        isTestnet: mode === 'test' ? 'true' : 'false',
      });

      // Primary source: balance-stats (canonical counts shared with Dashboard/Balance).
      // Supplemental: legacy /invoices/stats, read only for the Cancelled count which
      // balance-stats does not return.
      const [balanceResponse, legacyResponse] = await Promise.all([
        fetchWithAuth(`${API_BASE_URL}/invoices/balance-stats?${params}`),
        fetchWithAuth(`${API_BASE_URL}/invoices/stats?${params}`).catch(() => null),
      ]);

      const balancePayload = await balanceResponse.json();

      let cancelledInvoice = 0;
      let legacyData = null;
      try {
        if (legacyResponse) {
          const legacyPayload = await legacyResponse.json();
          if (legacyPayload.success && legacyPayload.data) {
            legacyData = legacyPayload.data;
            cancelledInvoice = legacyData.cancelledInvoice || 0;
          }
        }
      } catch (legacyError) {
        console.error("Failed to read supplemental invoice stats:", legacyError);
      }

      if (seq !== statsSeq.current) return;

      if (balancePayload.success && balancePayload.data) {
        const data = balancePayload.data;
        const counts = deriveCounts(data);

        setInvoiceStats({
          totalInvoices: counts.invoice.total,
          paidInvoices: counts.invoice.paidCount,
          // Excludes overpaid. The Paid TAB filters on status='paid' exactly, so
          // its badge must not include overpaid invoices — that is what showed
          // 113 (83 paid + 30 overpaid) above a list of 83 rows.
          fullyPaidInvoices: counts.invoice.fullyPaidCount,
          partiallyPaidInvoices: counts.invoice.partiallyPaidCount,
          overpaidInvoices: counts.invoice.overpaidCount,
          overdueInvoices: counts.invoice.overdueCount,
          pendingInvoices: counts.invoice.pendingCount,
          unsupportedInvoices:
            counts.invoice.unsupportedCount || legacyData?.unsupportedInvoices || 0,
          cancelledInvoice,
          // INVOICE figures only. balance-stats' top-level totalPaid/totalRemaining
          // are the merchant's combined revenue (invoices + checkout sessions +
          // standalone deposits), which is right for the Dashboard but not for a
          // card on the Invoices page: a merchant with no invoices was shown
          // their direct deposits as "Paid" over "0 paid".
          totalPaid: data.invoices?.totalPaid ?? legacyData?.totalPaid ?? 0,
          totalRemaining: data.invoices?.totalRemaining ?? legacyData?.totalRemaining ?? 0,
          totalInvoiceAmount: data.totalInvoiceAmount != null ? data.totalInvoiceAmount : (legacyData?.totalInvoiceAmount || 0),
          totalOverdueAmount: data.totalOverdueAmount != null ? data.totalOverdueAmount : (data.invoices?.totalOverdueAmount || 0),
          avgPaymentSeconds:
            data.avgPaymentSeconds != null
              ? data.avgPaymentSeconds
              : (data.invoices?.avgPaymentSeconds ?? legacyData?.avgPaymentSeconds ?? null),
          avgPaymentSampleSize:
            data.avgPaymentSampleSize != null
              ? data.avgPaymentSampleSize
              : (data.invoices?.avgPaymentSampleSize ?? legacyData?.avgPaymentSampleSize ?? 0),
        });
      }
    } catch (error) {
      if (seq !== statsSeq.current) return;
      console.error("Failed to fetch invoice stats:", error);
      setStatsError(true);
    }
  }, [mode]);

  // Every mutation changes both the page of rows and the KPI counts above it.
  // Refreshing only the list left "Total invoices" reading the old number until
  // a manual reload, so both go through here.
  const refreshInvoiceData = useCallback(() => {
    fetchInvoices();
    fetchInvoiceStats();
  }, [fetchInvoices, fetchInvoiceStats]);

  const fetchMerchants = useCallback(async () => {
    if (!isSuperAdmin) return;

    setIsMerchantsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetchWithAuth(`${API_BASE_URL}/merchants?limit=100`);
      const data = await response.json();

      if (data.success && data.data) {
        setMerchants(data.data.merchants || []);
      }
    } catch (error) {
      console.error("Failed to fetch merchants:", error);
    } finally {
      setIsMerchantsLoading(false);
    }
  }, [isSuperAdmin]);

  // BUG-05: export ALL invoices matching the current filters (server-side, not just
  // the page). Gated by the csv_pdf_export plan feature (403 → prompt to upgrade).
  const handleExportInvoices = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const params = new URLSearchParams({ sortBy, sortOrder });
      params.append("isTestnet", mode === "test" ? "true" : "false");
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (debouncedSearchTerm) params.append("search", debouncedSearchTerm);
      if (isSuperAdmin && merchantFilter && merchantFilter !== "all") {
        params.append("merchantId", merchantFilter);
      }

      const response = await fetchWithAuth(
        `${API_BASE_URL}/invoices/export?${params.toString()}`
      );
      if (response.status === 403) {
        toast.error("CSV export is a Premium feature. Upgrade your plan to export invoices.");
        return;
      }
      if (!response.ok) {
        toast.error("Failed to export invoices");
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export invoices failed:", error);
      toast.error("Failed to export invoices");
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, sortBy, sortOrder, mode, statusFilter, debouncedSearchTerm, isSuperAdmin, merchantFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    fetchInvoiceStats();
  }, [fetchInvoiceStats]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchMerchants();
    }
  }, [isSuperAdmin, fetchMerchants]);

  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatStatusDisplay = (status) => {
    if (!status) return "Draft";

    const statusMap = {
      'draft': 'Draft',
      'open': 'Open',
      'pending': 'Pending',
      'paid': 'Paid',
      'partially_paid': 'Partially Paid',
      'paid_unsupported_token': 'Unsupported Token',
      'overpaid': 'Overpaid',
      'overdue': 'Overdue',
      'cancelled': 'Cancelled',
      'refunded': 'Refunded',
    };

    return statusMap[status.toLowerCase()] || status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };


  const handleDropdownToggle = (e, invoiceId) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === invoiceId ? null : invoiceId);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
      setShowFilterDropdown(false);
      setShowColumnDropdown(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Re-render the freshness line ("Updated Xs ago") roughly every 10s.
  useEffect(() => {
    const id = setInterval(() => setFreshTick((t) => t + 1), 10000);
    return () => clearInterval(id);
  }, []);

  const updatedText = useMemo(() => {
    void freshTick; // recompute on tick
    if (!lastUpdated) return "";
    const secs = Math.max(0, Math.round((Date.now() - lastUpdated) / 1000));
    if (secs < 5) return "Updated just now";
    if (secs < 60) return `Updated ${secs}s ago`;
    const mins = Math.round(secs / 60);
    if (mins < 60) return `Updated ${mins}m ago`;
    const hrs = Math.round(mins / 60);
    return `Updated ${hrs}h ago`;
  }, [lastUpdated, freshTick]);

  // KPI derivations. Outstanding/Paid totals come from balance-stats (real data).
  // Total overdue $ is a server-side aggregate over ALL invoices (BUG-06), so it stays
  // correct across pagination. Average days-late is still derived from the loaded page
  // (a secondary signal with no backend aggregate); falls back to "—" when not computable.
  const overdueStats = useMemo(() => {
    const today = new Date();
    let lateDaysTotal = 0;
    let count = 0;
    invoices.forEach((inv) => {
      if (String(inv.status).toLowerCase() !== "overdue") return;
      count += 1;
      if (inv.due_date) {
        const due = new Date(inv.due_date);
        const days = Math.floor((today - due) / 86400000);
        if (days > 0) lateDaysTotal += days;
      }
    });
    return {
      sum: invoiceStats.totalOverdueAmount || 0,
      avgDays: count > 0 && lateDaysTotal > 0 ? Math.round(lateDaysTotal / count) : null,
    };
  }, [invoices, invoiceStats.totalOverdueAmount]);

  // Average time from invoice creation to full settlement. Server-side aggregate
  // over ALL settled invoices (paid + overpaid) for the current mode, so it does
  // not drift with pagination. Settled rows with no paid_at recorded are excluded
  // by the backend rather than counted as instant.
  const avgPaymentTime = useMemo(() => {
    const label = formatDuration(invoiceStats.avgPaymentSeconds);
    const sample = invoiceStats.avgPaymentSampleSize || 0;

    if (label === null || sample === 0) {
      return { value: "—", sub: "No data" };
    }

    return {
      value: label,
      sub: `across ${formatCount(sample)} paid ${sample === 1 ? "invoice" : "invoices"}`,
    };
  }, [invoiceStats.avgPaymentSeconds, invoiceStats.avgPaymentSampleSize]);

  // Map of invoiceId -> days late, for the DUE DATE column "Xd late" treatment.
  const daysLateById = useMemo(() => {
    const today = new Date();
    const map = {};
    invoices.forEach((inv) => {
      if (String(inv.status).toLowerCase() !== "overdue" || !inv.due_date) return;
      const due = new Date(inv.due_date);
      const days = Math.floor((today - due) / 86400000);
      if (days > 0) map[inv.id] = days;
    });
    return map;
  }, [invoices]);

  const openCount =
    (invoiceStats.pendingInvoices || 0) + (invoiceStats.partiallyPaidInvoices || 0);

  // Tab key must be a real invoice status: the list sends it straight through as
  // ?status=. 'open' resolves to open+pending server-side so the rows match the
  // count shown here.
  const filterTabs = [
    { key: "all", label: "All", count: formatCount(invoiceStats.totalInvoices) },
    { key: "open", label: "Open", count: formatCount(invoiceStats.pendingInvoices) },
    { key: "paid", label: "Paid", count: formatCount(invoiceStats.fullyPaidInvoices) },
    { key: "overdue", label: "Overdue", count: formatCount(invoiceStats.overdueInvoices) },
    {
      key: "paid_unsupported_token",
      label: "Unsupported",
      count: formatCount(invoiceStats.unsupportedInvoices),
    },
  ];

  // Onboarding deep-link: open the create drawer prefilled with a sample test
  // invoice so the merchant creates their first invoice inside the real tool.
  const fromOnboarding = searchParams.get("onboarding") === "1";
  useEffect(() => {
    if (!fromOnboarding) return;
    setNextInvoiceId(generateUniqueInvoiceId());
    setInvoiceForm({
      customerName: "Test customer",
      customerEmail: "",
      amount: "",
      dueDate: getTomorrowDate(),
      description: "",
      memo: "",
    });
    setLineItems([
      { id: 1, description: "Sample item", quantity: 1, unitPrice: "10", amount: 10 },
    ]);
    setShowCreateDrawer(true);
  }, [fromOnboarding]);

  // Deep-link from Overview "Create invoice" buttons (?action=create) — open the
  // create drawer with a fresh, empty invoice (no sample data).
  const openCreateFromQuery = searchParams.get("action") === "create";
  useEffect(() => {
    if (!openCreateFromQuery || fromOnboarding) return;
    setNextInvoiceId(generateUniqueInvoiceId());
    setInvoiceForm({
      customerName: "",
      customerEmail: "",
      amount: "",
      dueDate: getTomorrowDate(),
      description: "",
      memo: "",
    });
    setShowCreateDrawer(true);
  }, [openCreateFromQuery, fromOnboarding]);

  const getVisibleColumnCount = () => {
    let count = 0;
    if (visibleColumns.invoice) count++;
    if (visibleColumns.customer) count++;
    if (visibleColumns.amount) count++;
    if (visibleColumns.asset) count++;
    if (visibleColumns.status) count++;
    if (visibleColumns.dueDate) count++;
    if (visibleColumns.created) count++;
    if (isSuperAdmin && visibleColumns.merchant) count++;
    if (visibleColumns.actions) count++;
    return count;
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetchWithAuth(`${API_BASE_URL}/invoices/${selectedInvoice.id}/cancel`, {
        method: "PATCH",
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Invoice cancelled successfully");
        refreshInvoiceData();
      } else {
        toast.error(data.message || "Failed to cancel invoice");
      }
    } catch (error) {
      console.error("Failed to cancel invoice:", error);
      toast.error("Failed to cancel invoice");
    } finally {
      setIsUpdating(false);
      setShowDeleteModal(false);
      setSelectedInvoice(null);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedInvoice || !newStatus) return;
    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetchWithAuth(`${API_BASE_URL}/invoices/${selectedInvoice.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Invoice status updated successfully");
        refreshInvoiceData();
      } else {
        toast.error(data.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    } finally {
      setIsUpdating(false);
      setShowStatusModal(false);
      setSelectedInvoice(null);
      setNewStatus("");
    }
  };

  const handleUpdateDueDate = async () => {
    if (!selectedInvoice || !newDueDate) return;
    setIsUpdating(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/invoices/${selectedInvoice.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ dueDate: new Date(newDueDate).toISOString() }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Due date updated successfully");
        refreshInvoiceData();
      } else {
        toast.error(data.message || "Failed to update due date");
      }
    } catch (error) {
      console.error("Failed to update due date:", error);
      toast.error("Failed to update due date");
    } finally {
      setIsUpdating(false);
      setShowDueDateModal(false);
      setSelectedInvoice(null);
      setNewDueDate("");
    }
  };

  const openDeleteModal = (e, invoice) => {
    e.stopPropagation();
    setSelectedInvoice(invoice);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const openStatusModal = (e, invoice) => {
    e.stopPropagation();
    setSelectedInvoice(invoice);
    setNewStatus(invoice.status || "draft");
    setShowStatusModal(true);
    setActiveDropdown(null);
  };

  const openDueDateModal = (e, invoice) => {
    e.stopPropagation();
    setSelectedInvoice(invoice);
    setNewDueDate(invoice.due_date ? invoice.due_date.split("T")[0] : getTomorrowDate());
    setShowDueDateModal(true);
    setActiveDropdown(null);
  };

  const handleRowClick = (invoice) => {
    navigate(`/dashboard/invoices/${invoice.id}`);
  };

  const handleCopyPaymentLink = async (e, invoice) => {
    e.stopPropagation();
    if (!invoice.linkId) {
      toast.error("Payment link is not available for this invoice");
      return;
    }
    const url = `${window.location.origin}/invoice/${invoice.linkId}`;
    try {
      // writeText rejects in an insecure context or when permission is denied —
      // confirm the copy happened before claiming it did.
      await navigator.clipboard.writeText(url);
      setCopiedLinkId(invoice.id);
      toast.success("Payment link copied");
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch (err) {
      console.error("Failed to copy payment link:", err);
      toast.error("Could not copy the link. Copy it from the invoice page instead.");
    }
  };

  const handleCloseDrawer = () => {
    setShowCreateDrawer(false);
    setIsDrawerMaximized(false);
    setInvoiceForm({
      customerName: "",
      customerEmail: "",
      amount: "",
      dueDate: "",
      description: "",
      memo: "",
    });
    setLineItems([
      { id: 1, description: "", quantity: 1, unitPrice: "", amount: 0 },
    ]);
    setGstValue("");
    setIncludeGst(false);
    setGstType("percentage");
    setClientNote("");
    setTermsConditions("");
    setShowPreview(false);
    setFormErrors({});
  };

  // ---- Drawer exit animation -------------------------------------------
  // handleCloseDrawer flips showCreateDrawer and unmounts the panel in the same
  // frame, so there was nothing left on screen to fade. requestCloseDrawer
  // marks the panel as leaving, lets CSS fade it out, and only then runs
  // handleCloseDrawer unchanged. The form reset is DEFERRED, never altered.
  //
  // Plain functions, not useCallback: they close over handleCloseDrawer, which
  // is re-created every render, so memoising would go stale or trip
  // exhaustive-deps. Neither is passed to a memoised child.
  const [drawerClosing, setDrawerClosing] = useState(false);
  const closeBailRef = useRef(null);

  const finishCloseDrawer = () => {
    if (closeBailRef.current) {
      clearTimeout(closeBailRef.current);
      closeBailRef.current = null;
    }
    setDrawerClosing(false);
    handleCloseDrawer();
  };

  const requestCloseDrawer = () => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      handleCloseDrawer();
      return;
    }
    setDrawerClosing((already) => {
      if (already) return already;
      // animationend is not guaranteed (backgrounded tab, interrupted
      // animation), so never leave the panel stuck mid-exit.
      closeBailRef.current = setTimeout(finishCloseDrawer, 400);
      return true;
    });
  };

  useEffect(() => () => {
    if (closeBailRef.current) clearTimeout(closeBailRef.current);
  }, []);

  const handleAddLineItem = () => {
    const newId =
      lineItems.length > 0
        ? Math.max(...lineItems.map((item) => item.id)) + 1
        : 1;
    setLineItems([
      ...lineItems,
      { id: newId, description: "", quantity: 1, unitPrice: "", amount: 0 },
    ]);
  };

  const handleRemoveLineItem = (id) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((item) => item.id !== id));
    }
  };

  const handleLineItemChange = (id, field, value) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          if (field === "quantity" || field === "unitPrice") {
            const qty =
              field === "quantity"
                ? parseFloat(value) || 0
                : parseFloat(item.quantity) || 0;
            const price =
              field === "unitPrice"
                ? parseFloat(value) || 0
                : parseFloat(item.unitPrice) || 0;
            updatedItem.amount = qty * price;
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const subtotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const gstAmount =
    includeGst && gstValue
      ? gstType === "percentage"
        ? (subtotal * parseFloat(gstValue)) / 100
        : parseFloat(gstValue)
      : 0;
  const calculatedTotal = subtotal + gstAmount;
  const grandTotal =
    calculatedTotal > 0 ? calculatedTotal : parseFloat(invoiceForm.amount) || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};

    if (!invoiceForm.customerName?.trim()) {
      errors.customerName = "Customer name is required";
    } else if (invoiceForm.customerName.trim().length > CUSTOMER_NAME_MAX) {
      errors.customerName = `Customer name cannot exceed ${CUSTOMER_NAME_MAX} characters`;
    } else if (!NAME_ALLOWED.test(invoiceForm.customerName)) {
      // The input already strips these, so this is the backstop for anything
      // that reaches state another way (autofill, restored draft).
      errors.customerName =
        "Name may contain only letters, spaces, hyphens, apostrophes and periods";
    }

    // Description is optional. Only its length is checked.
    if ((invoiceForm.description || "").trim().length > DESCRIPTION_MAX) {
      errors.description = `Description cannot exceed ${DESCRIPTION_MAX} characters`;
    } else if (hasHtmlTag(invoiceForm.description)) {
      errors.description = `Description ${HTML_TAG_MESSAGE}`;
    }

    if (clientNote.trim().length > CLIENT_NOTE_MAX) {
      errors.clientNote = `Client note cannot exceed ${CLIENT_NOTE_MAX} characters`;
    } else if (hasHtmlTag(clientNote)) {
      errors.clientNote = `Client note ${HTML_TAG_MESSAGE}`;
    }

    if (termsConditions.trim().length > TERMS_MAX) {
      errors.termsConditions = `Terms & conditions cannot exceed ${TERMS_MAX} characters`;
    } else if (hasHtmlTag(termsConditions)) {
      errors.termsConditions = `Terms & conditions ${HTML_TAG_MESSAGE}`;
    }

    if (!invoiceForm.customerEmail?.trim()) {
      errors.customerEmail = "Customer email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceForm.customerEmail)) {
      errors.customerEmail = "Please enter a valid email address";
    }

    const lineItemErrors = {};
    lineItems.forEach((item, index) => {
      const itemErrors = {};

      const qty = parseFloat(item.quantity);
      if (isNaN(qty) || qty <= 0) {
        itemErrors.quantity = "Quantity must be greater than 0";
      }

      if (!item.description || !item.description.trim()) {
        itemErrors.description = "Item description is required";
      } else if (item.description.trim().length > LINE_ITEM_DESCRIPTION_MAX) {
        itemErrors.description = `Item description cannot exceed ${LINE_ITEM_DESCRIPTION_MAX} characters`;
      } else if (hasHtmlTag(item.description)) {
        itemErrors.description = `Item description ${HTML_TAG_MESSAGE}`;
      }

      const price = parseFloat(item.unitPrice);
      if (!isNaN(price) && price < 0) {
        itemErrors.unitPrice = "Unit price cannot be negative";
      }

      if (Object.keys(itemErrors).length > 0) {
        lineItemErrors[item.id] = itemErrors;
      }
    });

    if (Object.keys(lineItemErrors).length > 0) {
      errors.lineItems = lineItemErrors;
    }

    if (grandTotal == 0) {
      errors.amount = "At least one item is required, and the total amount cannot be zero or negative!!"
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstError = errors.customerName || errors.customerEmail || errors.description ||
        (errors.lineItems && Object.values(errors.lineItems)[0]?.description) ||
        (errors.lineItems && Object.values(errors.lineItems)[0]?.quantity) ||
        (errors.lineItems && Object.values(errors.lineItems)[0]?.unitPrice) ||
        errors.amount || errors.clientNote || errors.termsConditions;
      toast.error(firstError || "Please fix the validation errors");
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");

      const formattedLineItems = lineItems.map((item) => ({
        description: item.description,
        quantity: parseFloat(item.quantity) || 0,
        unitPrice: parseFloat(item.unitPrice) || 0,
        amount: item.amount || 0,
      }));

      const payload = {
        customerName: invoiceForm.customerName,
        customerEmail: invoiceForm.customerEmail,
        amount: grandTotal,
        currency: "USD",
        description: invoiceForm.description?.trim() || undefined,
        dueDate: invoiceForm.dueDate
          ? new Date(invoiceForm.dueDate).toISOString()
          : undefined,
        lineItems: formattedLineItems,
        isTestnet: mode === 'test',
        metadata: {
          subtotal,
          gstType: includeGst ? gstType : null,
          gstValue: includeGst ? parseFloat(gstValue) : 0,
          gstPercentage: includeGst && gstType === "percentage" ? parseFloat(gstValue) : 0,
          gstAmount,
          clientNote,
          termsConditions,
        },
      };

      const response = await fetchWithAuth(`${API_BASE_URL}/invoices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Invoice created successfully");
        requestCloseDrawer();
        // Bring the new invoice into view. The refresh alone re-ran the CURRENT
        // query, so on page 2, under a status tab a new invoice is not in
        // (Paid, Overdue...), behind a search, or with another sort, the
        // invoice just created was nowhere on screen. Newest-first, page 1,
        // all statuses, no search puts it at the top. Changing any of these
        // re-runs fetchInvoices through its effect; the explicit refresh covers
        // the case where nothing changed, and the request-sequence guard in
        // fetchInvoices makes sure the newest query is the one shown.
        setStatusFilter("all");
        setSearchTerm("");
        setSortBy("created_at");
        setSortOrder("desc");
        setCurrentPage(1);
        refreshInvoiceData();
      } else {
        console.error("Failed to create invoice:", data.message);
        toast.error(data.message || "Failed to create invoice");
      }
    } catch (error) {
      console.error("Failed to create invoice:", error);
      toast.error("Failed to create invoice. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const mockAddress = "0x742d35Cc6634C0532925a3b8448c9e7595f...";

  // No public link/linkId exists while creating an invoice, so the preview QR
  // stays a neutral placeholder until the invoice is created.
  const previewLinkId = null;

  return (
    <div className="ent ent-page invoices-page invoices-page--ent">
      {fromOnboarding && (
        <div className="onboarding-return-banner">
          <span>
            You are setting up MaxelPay. Create this test invoice, then head back
            to finish setup.
          </span>
          <button
            type="button"
            className="onboarding-return-btn"
            onClick={() => navigate("/onboarding")}
          >
            Back to setup
          </button>
        </div>
      )}
      <PageHeaderPro
        title="Invoices"
        mode={mode === "test" ? "test" : "live"}
        updatedText={updatedText}
        actions={
          <>
            <button
              className="ent-btn ent-btn-secondary"
              type="button"
              onClick={handleExportInvoices}
              disabled={isExporting}
            >
              {isExporting ? "Exporting…" : "Export"}
            </button>
            <button
              className="ent-btn ent-btn-primary"
              type="button"
              onClick={() => {
                setNextInvoiceId(generateUniqueInvoiceId());
                setInvoiceForm({
                  customerName: "",
                  customerEmail: "",
                  amount: "",
                  dueDate: getTomorrowDate(),
                  description: "",
                  memo: "",
                });
                setShowCreateDrawer(true);
              }}
            >
              <Plus size={16} />
              Create invoice
            </button>
          </>
        }
      />

      {/* Same card as the transactions summary and the balance page: tinted
          icon tile, label, one figure, one qualifier. */}
      <div className="inv-stats">
        <StatCard
          tone="info"
          icon={FileClock}
          label="Outstanding"
          value={`$${formatUSD(invoiceStats.totalRemaining)}`}
          sub={`${formatCount(openCount)} open invoices`}
        />
        <StatCard
          tone="ok"
          icon={CheckCircle2}
          label="Paid"
          value={`$${formatUSD(invoiceStats.totalPaid)}`}
          sub={`${formatCount(invoiceStats.paidInvoices)} paid`}
        />
        <StatCard
          tone="err"
          icon={AlertTriangle}
          label="Overdue"
          value={`$${formatUSD(overdueStats.sum)}`}
          // The figure stays red: on this card the number IS the warning.
          valueTone="err"
          sub={
            overdueStats.avgDays != null
              ? `${formatCount(invoiceStats.overdueInvoices)} invoices · ${overdueStats.avgDays} days avg`
              : `${formatCount(invoiceStats.overdueInvoices)} invoices`
          }
        />
        <StatCard
          tone="brand"
          icon={Clock}
          label="Avg payment time"
          value={avgPaymentTime.value}
          sub={avgPaymentTime.sub}
        />
      </div>

      {statsError && (
        <div className="stats-error">
          <AlertTriangle size={16} />
          <span>Could not load invoice stats.</span>
          <button className="btn-toolbar" onClick={() => fetchInvoiceStats()}>
            Retry
          </button>
        </div>
      )}

      <FilterTabs
        tabs={filterTabs}
        active={statusFilter}
        onChange={(key) => {
          setStatusFilter(key);
          setCurrentPage(1);
        }}
        right={
          <div className="invoices-inline-controls">
            <EntSearch
              value={searchTerm}
              onChange={(val) => {
                setSearchTerm(val);
                setCurrentPage(1);
              }}
              placeholder="Search by invoice #, customer, amount…"
            />
          <div className="dropdown-container">
            <button
              className="btn-toolbar"
              onClick={(e) => {
                e.stopPropagation();
                setShowFilterDropdown(!showFilterDropdown);
                setShowColumnDropdown(false);
              }}
            >
              <Filter size={16} />
              Quick Filters
            </button>
            {showFilterDropdown && (
              <div className="dropdown-menu filter-dropdown" onClick={(e) => e.stopPropagation()}>
                <div className="dropdown-section">
                  <div className="dropdown-header">Status</div>
                  {[
                    { id: 'all', label: 'All Status' },
                    { id: 'open', label: 'Open' },
                    { id: 'paid', label: 'Paid' },
                    { id: 'overpaid', label: 'Overpaid' },
                    { id: 'partially_paid', label: 'Partially Paid' },
                    { id: 'overdue', label: 'Overdue' },
                    { id: 'paid_unsupported_token', label: 'Unsupported' },
                    { id: 'cancelled', label: 'Cancelled' },
                  ].map((status) => (
                    <button
                      key={status.id}
                      className={`dropdown-item ${statusFilter === status.id ? 'active' : ''}`}
                      onClick={() => {
                        setStatusFilter(status.id);
                        setCurrentPage(1);
                      }}
                    >
                      {statusFilter === status.id && <Check size={14} />}
                      {status.label}
                    </button>
                  ))}
                </div>
                {isSuperAdmin && (
                  <>
                    <div className="dropdown-divider" />
                    <div className="dropdown-section">
                      <div className="dropdown-header">Merchant</div>
                      <button
                        className={`dropdown-item ${merchantFilter === 'all' ? 'active' : ''}`}
                        onClick={() => {
                          setMerchantFilter('all');
                          setCurrentPage(1);
                        }}
                      >
                        {merchantFilter === 'all' && <Check size={14} />}
                        All Merchants
                      </button>
                      {isMerchantsLoading ? (
                        <div className="dropdown-item disabled">Loading...</div>
                      ) : (
                        merchants.map((merchant) => (
                          <button
                            key={merchant.id}
                            className={`dropdown-item ${merchantFilter === merchant.id.toString() ? 'active' : ''}`}
                            onClick={() => {
                              setMerchantFilter(merchant.id.toString());
                              setCurrentPage(1);
                            }}
                          >
                            {merchantFilter === merchant.id.toString() && <Check size={14} />}
                            <div className="merchant-filter-item">
                              <span>{merchant.businessName || `${merchant.firstName} ${merchant.lastName}`}</span>
                              <span className="merchant-email-hint">{merchant.email}</span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="dropdown-container">
            <button
              className="btn-toolbar"
              onClick={(e) => {
                e.stopPropagation();
                setShowColumnDropdown(!showColumnDropdown);
                setShowFilterDropdown(false);
              }}
            >
              <Columns size={16} />
              Columns
            </button>
            {showColumnDropdown && (
              <div className="dropdown-menu column-dropdown" onClick={(e) => e.stopPropagation()}>
                <div className="dropdown-header">
                  Show/Hide Columns
                  <button
                    className="reset-columns-btn"
                    onClick={() =>
                      setVisibleColumns({
                        invoice: true,
                        amount: true,
                        asset: true,
                        customer: true,
                        status: true,
                        dueDate: true,
                        created: true,
                        merchant: true,
                        actions: true,
                      })
                    }
                  >
                    Reset
                  </button>
                </div>
                {Object.entries(visibleColumns)
                  .filter(([key]) => key !== 'merchant' || isSuperAdmin)
                  .map(([key, visible]) => {
                    const isLocked = LOCKED_COLUMNS.includes(key);
                    return (
                      <label
                        key={key}
                        className={`dropdown-checkbox${isLocked ? ' is-locked' : ''}`}
                        title={isLocked ? 'This column is always shown' : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={visible}
                          disabled={isLocked}
                          onChange={() => {
                            if (isLocked) return;
                            setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
                          }}
                        />
                        <span className="checkbox-custom">
                          {visible && <Check size={12} />}
                        </span>
                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                      </label>
                    );
                  })}
              </div>
            )}
          </div>

          {(statusFilter !== 'all' || merchantFilter !== 'all' || searchTerm) && (
            <button
              className="btn-clear-filters"
              onClick={() => {
                setStatusFilter('all');
                setMerchantFilter('all');
                setSearchTerm('');
                setCurrentPage(1);
              }}
            >
              <X size={14} />
              Clear all
            </button>
          )}
          </div>
        }
      />

      <div className="pro-table-wrap invoices-pro-table">
        <table className="pro-table">
          <thead>
            <tr>
              {visibleColumns.invoice && (
                <th onClick={() => handleSortChange("id")} className="sortable">
                  Invoice #{sortBy === "id" ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </th>
              )}
              {visibleColumns.customer && (
                <th onClick={() => handleSortChange("customer_name")} className="sortable">
                  Customer{sortBy === "customer_name" ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </th>
              )}
              {visibleColumns.amount && (
                <th onClick={() => handleSortChange("amount")} className="sortable amount-cell">
                  Amount{sortBy === "amount" ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </th>
              )}
              {visibleColumns.asset && <th>Asset</th>}
              {visibleColumns.created && (
                <th onClick={() => handleSortChange("created_at")} className="sortable">
                  Issued{sortBy === "created_at" ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </th>
              )}
              {visibleColumns.dueDate && (
                <th onClick={() => handleSortChange("due_date")} className="sortable">
                  Due date{sortBy === "due_date" ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </th>
              )}
              {isSuperAdmin && visibleColumns.merchant && <th>Merchant</th>}
              {visibleColumns.status && (
                <th onClick={() => handleSortChange("status")} className="sortable">
                  Status{sortBy === "status" ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </th>
              )}
              {visibleColumns.actions && <th className="col-kebab" />}
            </tr>
          </thead>
          <tbody>
            {isLoadingInvoices ? (
              <tr>
                <td colSpan={getVisibleColumnCount()} className="empty-table">
                  <TableSkeleton rows={pageSize > 10 ? 10 : pageSize} cols={getVisibleColumnCount()} />
                </td>
              </tr>
            ) : invoicesError ? (
              <tr>
                <td colSpan={getVisibleColumnCount()} className="empty-table">
                  <EmptyState
                    icon={<AlertTriangle size={24} />}
                    title="Could not load invoices"
                    subtitle="Something went wrong while fetching your invoices."
                    action={{ label: "Retry", onClick: () => fetchInvoices() }}
                  />
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={getVisibleColumnCount()} className="empty-table">
                  <EmptyState
                    icon={<FileText size={24} />}
                    title="No invoices yet"
                    subtitle="Create your first invoice."
                    action={{
                      label: "Create Invoice",
                      icon: <Plus size={16} />,
                      onClick: () => {
                        setNextInvoiceId(generateUniqueInvoiceId());
                        setInvoiceForm({
                          customerName: "",
                          customerEmail: "",
                          amount: "",
                          dueDate: getTomorrowDate(),
                          description: "",
                          memo: "",
                        });
                        setShowCreateDrawer(true);
                      },
                    }}
                  />
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => {
                const isDraft = String(invoice.status).toLowerCase() === "draft";
                const lateDays = daysLateById[invoice.id];
                return (
                  <tr
                    key={invoice.id}
                    onClick={() => handleRowClick(invoice)}
                    className={`clickable-row${isDraft ? " row-muted" : ""}`}
                  >
                    {visibleColumns.invoice && (
                      <td data-label="Invoice #">
                        <span className="mono inv-id">{invoice.id}</span>
                      </td>
                    )}
                    {visibleColumns.customer && (
                      <td data-label="Customer">
                        <div className="cell-2">
                          <span className="cell-2__main">{invoice.customer_name || "—"}</span>
                          <span className="cell-2__sub">{invoice.customer_email || ""}</span>
                        </div>
                      </td>
                    )}
                    {visibleColumns.amount && (
                      <td data-label="Amount" className="amount-cell">
                        <span className="amount-cell__main">${formatUSD(invoice.amount)}</span>
                        <span className="amount-cell__sub">{invoice.currency || "USD"}</span>
                      </td>
                    )}
                    {visibleColumns.asset && (
                      <td data-label="Asset">
                        {/* No token yet is normal: AssetBadge draws its
                            pending placeholder at the same diameter so the
                            logo column stays aligned. */}
                        <AssetBadge
                          symbol={invoice.token}
                          chain={invoice.network}
                          pendingLabel="Not selected"
                          pendingHint="Chosen at checkout"
                        />
                      </td>
                    )}
                    {visibleColumns.created && (
                      <td data-label="Issued">{formatShortDate(invoice.created_at)}</td>
                    )}
                    {visibleColumns.dueDate && (
                      <td data-label="Due date">
                        {lateDays ? (
                          <span className="due-late">
                            {formatShortDate(invoice.due_date)}
                            <span className="due-late__tag"> · {lateDays}d late</span>
                          </span>
                        ) : (
                          formatShortDate(invoice.due_date)
                        )}
                      </td>
                    )}
                    {isSuperAdmin && visibleColumns.merchant && (
                      <td data-label="Merchant" className="merchant-cell">
                        {invoice.merchant ? (
                          <div className="cell-2">
                            <span className="cell-2__main">{invoice.merchant.name}</span>
                            <span className="cell-2__sub">{invoice.merchant.email}</span>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td data-label="Status">
                        <StatusBadge
                          status={invoice.status}
                          label={formatStatusDisplay(invoice.status)}
                          dot
                        />
                      </td>
                    )}
                    {visibleColumns.actions && (
                      <td className="col-kebab">
                        <div className="dropdown-container">
                          <button
                            className="row-kebab"
                            onClick={(e) => handleDropdownToggle(e, invoice.id)}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <MoreVertical size={16} />
                          </button>
                          {activeDropdown === invoice.id && (
                            <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(invoice);
                                }}
                              >
                                <Eye size={14} />
                                View invoice
                              </button>
                              <button
                                className="dropdown-item"
                                onClick={(e) => handleCopyPaymentLink(e, invoice)}
                              >
                                {copiedLinkId === invoice.id ? <Check size={14} /> : <Copy size={14} />}
                                Copy payment link
                              </button>
                              {/* A settled invoice's status is final — editing it
                                  back to an unpaid state would discard the record
                                  of payment. The backend rejects it with
                                  INVOICE_FINAL, so the action is not offered. */}
                              {/* {!["paid", "overpaid", "refunded", "cancelled"].includes(
                                invoice.status
                              ) && (
                                <button
                                  className="dropdown-item"
                                  onClick={(e) => openStatusModal(e, invoice)}
                                >
                                  <Edit3 size={14} />
                                  Edit status
                                </button>
                              )} */}
                              {!["paid", "overpaid", "refunded", "cancelled"].includes(
                                invoice.status
                              ) && (
                                <button
                                  className="dropdown-item"
                                  onClick={(e) => openDueDateModal(e, invoice)}
                                >
                                  <Calendar size={14} />
                                  Change due date
                                </button>
                              )}
                              {!["paid", "overpaid", "partially_paid", "cancelled"].includes(invoice.status) && (
                                <>
                                  <div className="dropdown-divider" />
                                  <button
                                    className="dropdown-item danger"
                                    onClick={(e) => openDeleteModal(e, invoice)}
                                  >
                                    <Trash2 size={14} />
                                    Cancel invoice
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card view — visible only on <=576px (table is hidden via CSS) */}
      <div className="invoice-cards-mobile">
        {isLoadingInvoices ? (
          <div className="invoice-mobile-empty">
            <TableSkeleton rows={4} cols={1} />
          </div>
        ) : invoicesError ? (
          <EmptyState
            icon={<AlertTriangle size={24} />}
            title="Could not load invoices"
            subtitle="Something went wrong while fetching your invoices."
            action={{ label: "Retry", onClick: () => fetchInvoices() }}
          />
        ) : invoices.length === 0 ? (
          <EmptyState
            icon={<FileText size={24} />}
            title="No invoices yet"
            subtitle="Create your first invoice."
            action={{
              label: "Create Invoice",
              icon: <Plus size={16} />,
              onClick: () => {
                setNextInvoiceId(generateUniqueInvoiceId());
                setInvoiceForm({
                  customerName: "",
                  customerEmail: "",
                  amount: "",
                  dueDate: getTomorrowDate(),
                  description: "",
                  memo: "",
                });
                setShowCreateDrawer(true);
              },
            }}
          />
        ) : (
          invoices.map((invoice) => (
            <div
              key={`mobile-${invoice.id}`}
              className="invoice-card-mobile"
              onClick={() => handleRowClick(invoice)}
            >
              <div className="invoice-card-mobile-header">
                <div className="invoice-card-mobile-id">
                  <FileText size={16} />
                  {invoice.id}
                </div>
                <div className="invoice-card-mobile-amount">
                  {formatUSD(invoice.amount)} {invoice.currency || ""}
                </div>
              </div>
              <div className="invoice-card-mobile-row">
                <span className="invoice-card-mobile-label">Customer</span>
                <span className="invoice-card-mobile-value">{invoice.customer_name}</span>
              </div>
              <div className="invoice-card-mobile-row">
                <span className="invoice-card-mobile-label">Email</span>
                <span className="invoice-card-mobile-value">{invoice.customer_email}</span>
              </div>
              <div className="invoice-card-mobile-footer">
                <StatusBadge
                  status={invoice.status}
                  label={formatStatusDisplay(invoice.status)}
                />
                <div className="invoice-card-mobile-actions">
                  <button
                    className="action-icon-btn"
                    onClick={(e) => { e.stopPropagation(); handleRowClick(invoice); }}
                    title="View invoice"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    className="action-icon-btn"
                    onClick={(e) => handleCopyPaymentLink(e, invoice)}
                    title="Copy payment link"
                  >
                    {copiedLinkId === invoice.id ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {invoices.length > 0 && (
        <div className="invoices-pager-row">
          <Pager
            page={currentPage}
            pageCount={totalPages}
            total={totalItems}
            pageSize={pageSize}
            onPage={(p) => setCurrentPage(p)}
          />
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select rd-select inv-select"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      {showCreateDrawer && (
        <>
          {/*
            `open` is not decorative. index.css and responsive/nav.css both ship
            .drawer-overlay as opacity:0 + visibility:hidden at every width,
            revealed only by .open -- the mobile-nav drawer contract that
            Sidebar.jsx and all four scrims in PaymentLinks.jsx opt into.
            Without it this scrim has always been invisible AND unclickable,
            because visibility:hidden also removes an element from hit testing.
          */}
          <div
            className={`drawer-overlay open ${drawerClosing ? "is-closing" : ""}`}
            onClick={requestCloseDrawer}
          />
          <div
            className={`invoice-drawer ${isDrawerMaximized ? "drawer-maximized" : ""} ${
              drawerClosing ? "is-closing" : ""
            }`}
            onAnimationEnd={(e) => {
              // Children animate too; only the panel's own exit ends the close.
              if (e.target !== e.currentTarget) return;
              if (drawerClosing) finishCloseDrawer();
            }}
          >
            <div className="drawer-header">
              <div className="drawer-header-left">
                <button className="close-btn" onClick={requestCloseDrawer}>
                  <X size={20} />
                </button>
                <div className="drawer-title-section">
                  <div className="drawer-icon-wrapper">
                    <FileText size={18} />
                  </div>
                  <div>
                    <h2>Create Invoice</h2>
                    {/* <span className="invoice-id-text">{nextInvoiceId}</span> */}
                  </div>
                </div>
              </div>
              <div className="drawer-header-right">
                <button
                  className="btn-icon-outline"
                  onClick={() => setIsDrawerMaximized(!isDrawerMaximized)}
                  title={isDrawerMaximized ? "Minimize" : "Maximize"}
                >
                  {isDrawerMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </button>
                <button
                  className="btn-outline"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? "Hide preview" : "Show preview"}
                </button>

                <button
                  className="btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Invoice"}
                </button>
              </div>
            </div>

            <div className="drawer-content">
              <div className="drawer-form">
                <div className="form-scroll">
                  <div className="form-section">
                    <div className="section-header ">
                      <span className="section-number">1</span>
                      <span className="section-title">Customer</span>
                    </div>
                    <div className="section-content">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Name *</label>
                          <input
                            type="text"
                            placeholder="John Doe"
                            maxLength={CUSTOMER_NAME_MAX}
                            className={formErrors.customerName ? 'input-error' : ''}
                            value={invoiceForm.customerName}
                            onChange={(e) => {
                              // Strip disallowed characters as they are typed or
                              // pasted, so the field cannot hold a value the API
                              // would reject on submit.
                              setInvoiceForm((prev) => ({
                                ...prev,
                                customerName: sanitizeName(e.target.value).slice(0, CUSTOMER_NAME_MAX),
                              }));
                              if (formErrors.customerName) {
                                setFormErrors(prev => {
                                  const { customerName, ...rest } = prev;
                                  return rest;
                                });
                              }
                            }}
                          />
                          {formErrors.customerName ? (
                            <span className="inline-error">{formErrors.customerName}</span>
                          ) : (
                            <span className="input-hint">
                              {invoiceForm.customerName.length}/{CUSTOMER_NAME_MAX} characters
                            </span>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Email *</label>
                          <input
                            type="email"
                            placeholder="john@example.com"
                            className={formErrors.customerEmail ? 'input-error' : ''}
                            value={invoiceForm.customerEmail}
                            onChange={(e) => {
                              setInvoiceForm((prev) => ({
                                ...prev,
                                customerEmail: stripEmoji(e.target.value),
                              }));
                              if (formErrors.customerEmail) {
                                setFormErrors(prev => {
                                  const { customerEmail, ...rest } = prev;
                                  return rest;
                                });
                              }
                            }}
                          />
                          {formErrors.customerEmail && (
                            <span className="inline-error">{formErrors.customerEmail}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="section-header">
                      <span className="section-number">2</span>
                      <span className="section-title">Payment Details</span>
                    </div>
                    <div className="section-content">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Due Date *</label>
                          <div className="date-input-wrapper">
                            <input
                              type="date"
                              value={invoiceForm.dueDate}
                              onChange={(e) => {
                                if (!acceptDueDate(e.target.value)) return;
                                setInvoiceForm((prev) => ({
                                  ...prev,
                                  dueDate: e.target.value,
                                }));
                              }}
                              min={getTodayDate()}
                              {...pickerOnlyProps}
                            />
                          </div>
                          <span className="input-hint">Due date must be today or a future date</span>
                        </div>
                        <div className="form-group ">
                          <label>GST & Tax</label>
                          <label className="gst-checkbox-label flex">
                            <input
                              type="checkbox"
                              checked={includeGst}
                              onChange={(e) =>
                                setIncludeGst(e.target.checked)
                              }
                            />
                            <span>Include GST/Tax</span>
                          </label>
                        </div>
                      </div>

                        <div className="form-group">
                          <label>Description</label>
                          <textarea
                            placeholder="What is this invoice for? (optional, visible to your customer)"
                            className={formErrors.description ? 'input-error' : ''}
                            value={invoiceForm.description}
                            maxLength={DESCRIPTION_MAX}
                            rows={2}
                            onChange={(e) => {
                              setInvoiceForm((prev) => ({
                                ...prev,
                                description: stripEmoji(e.target.value),
                              }));
                              if (formErrors.description) {
                                setFormErrors((prev) => {
                                  const { description, ...rest } = prev;
                                  return rest;
                                });
                              }
                            }}
                          />
                          {formErrors.description ? (
                            <span className="inline-error">{formErrors.description}</span>
                          ) : (
                            <span className="input-hint">
                              {invoiceForm.description.length}/{DESCRIPTION_MAX} characters
                            </span>
                          )}
                        </div>

                      {includeGst && (
                        <div className="form-row">
                          <div className="form-group">
                            <label>GST Type</label>
                            <select
                              value={gstType}
                              onChange={(e) => setGstType(e.target.value)}
                            >
                              <option value="percentage">Percentage (%)</option>
                              <option value="fixed">Fixed Amount</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>
                              {gstType === "percentage" ? "GST Percentage" : "GST Amount"}
                            </label>
                            <div className="gst-input-wrapper">
                              <input
                                type="number"
                                placeholder={gstType === "percentage" ? "e.g., 18" : "e.g., 100"}
                                value={gstValue}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const numVal = parseFloat(val);
                                  if (val === '' || numVal >= 0) {
                                    if (gstType === "percentage" && numVal > 100) {
                                      setGstValue("100");
                                    } else {
                                      setGstValue(val);
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === '-' || e.key === 'e') {
                                    e.preventDefault();
                                  }
                                }}
                                min="0"
                                max={gstType === "percentage" ? "100" : undefined}
                                step="0.01"
                              />
                              <span className="gst-suffix">
                                {gstType === "percentage" ? "%" : "$"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="section-header">
                      <span className="section-number">3</span>
                      <span className="section-title">Items</span>
                    </div>
                    <div className="section-content">
                      <div className="line-items-table">
                        <div className="line-items-header">
                          <span className="col-description ">Description *</span>
                          <span className="col-qty">Qty</span>
                          <span className="col-price">Unit Price</span>
                          <span className="col-amount">Amount</span>
                          <span className="col-action"></span>
                        </div>
                        {lineItems.map((item) => (
                          <div key={item.id} className="line-item-row-wrapper">
                            <div className="line-item-row">
                              <input
                                type="text"
                                className={`col-description ${formErrors.lineItems?.[item.id]?.description ? 'input-error' : ''}`}
                                placeholder="Item description"
                                value={item.description}
                                maxLength={LINE_ITEM_DESCRIPTION_MAX}
                                onChange={(e) => {
                                  handleLineItemChange(
                                    item.id,
                                    "description",
                                    stripEmoji(e.target.value)
                                  );
                                  if (formErrors.lineItems?.[item.id]?.description) {
                                    setFormErrors(prev => {
                                      const newErrors = { ...prev };
                                      if (newErrors.lineItems?.[item.id]) {
                                        delete newErrors.lineItems[item.id].description;
                                        if (Object.keys(newErrors.lineItems[item.id]).length === 0) {
                                          delete newErrors.lineItems[item.id];
                                        }
                                        if (Object.keys(newErrors.lineItems).length === 0) {
                                          delete newErrors.lineItems;
                                        }
                                      }
                                      return newErrors;
                                    });
                                  }
                                }}
                              />
                              <div className="col-qty-wrapper">
                                <input
                                  type="number"
                                  className={`col-qty ${formErrors.lineItems?.[item.id]?.quantity ? 'input-error' : ''}`}
                                  placeholder="1"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    handleLineItemChange(
                                      item.id,
                                      "quantity",
                                      e.target.value
                                    );
                                    if (formErrors.lineItems?.[item.id]?.quantity) {
                                      setFormErrors(prev => {
                                        const newErrors = { ...prev };
                                        if (newErrors.lineItems?.[item.id]) {
                                          delete newErrors.lineItems[item.id].quantity;
                                          if (Object.keys(newErrors.lineItems[item.id]).length === 0) {
                                            delete newErrors.lineItems[item.id];
                                          }
                                          if (Object.keys(newErrors.lineItems).length === 0) {
                                            delete newErrors.lineItems;
                                          }
                                        }
                                        return newErrors;
                                      });
                                    }
                                  }}
                                  min="1"
                                />
                              </div>
                              <div className="col-price-wrapper">
                                <input
                                  type="number"
                                  className={`col-price ${formErrors.lineItems?.[item.id]?.unitPrice ? 'input-error' : ''}`}
                                  placeholder="0.00"
                                  value={item.unitPrice}
                                  onChange={(e) => {
                                    handleLineItemChange(
                                      item.id,
                                      "unitPrice",
                                      e.target.value
                                    );
                                    if (formErrors.lineItems?.[item.id]?.unitPrice) {
                                      setFormErrors(prev => {
                                        const newErrors = { ...prev };
                                        if (newErrors.lineItems?.[item.id]) {
                                          delete newErrors.lineItems[item.id].unitPrice;
                                          if (Object.keys(newErrors.lineItems[item.id]).length === 0) {
                                            delete newErrors.lineItems[item.id];
                                          }
                                          if (Object.keys(newErrors.lineItems).length === 0) {
                                            delete newErrors.lineItems;
                                          }
                                        }
                                        return newErrors;
                                      });
                                    }
                                  }}
                                  step="0.01"
                                  min="0"
                                />
                              </div>
                              <span className="col-amount item-amount">
                                {formatUSD(item.amount)}
                              </span>
                              <button
                                type="button"
                                className="col-action remove-item-btn"
                                onClick={() => handleRemoveLineItem(item.id)}
                                disabled={lineItems.length === 1}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                            {(formErrors.lineItems?.[item.id]?.description || formErrors.lineItems?.[item.id]?.quantity || formErrors.lineItems?.[item.id]?.unitPrice) && (
                              <div className="line-item-errors">
                                {formErrors.lineItems?.[item.id]?.description && (
                                  <span className="inline-error">{formErrors.lineItems[item.id].description}</span>
                                )}
                                {formErrors.lineItems?.[item.id]?.quantity && (
                                  <span className="inline-error">{formErrors.lineItems[item.id].quantity}</span>
                                )}
                                {formErrors.lineItems?.[item.id]?.unitPrice && (
                                  <span className="inline-error">{formErrors.lineItems[item.id].unitPrice}</span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          className="add-item-btn"
                          onClick={handleAddLineItem}
                        >
                          <Plus size={16} />
                          Add Item
                        </button>
                      </div>

                      <div className="invoice-totals">
                        <div className="total-row">
                          <span>Subtotal</span>
                          <span>${formatUSD(subtotal)}</span>
                        </div>
                        {includeGst && gstValue && (
                          <div className="total-row">
                            <span>GST {gstType === "percentage" ? `(${gstValue}%)` : "(Fixed)"}</span>
                            <span>${formatUSD(gstAmount)}</span>
                          </div>
                        )}
                        <div className="total-row grand-total">
                          <span>Total</span>
                          <span>${formatUSD(grandTotal)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="section-header">
                      <span className="section-number">4</span>
                      <span className="section-title">Notes & Terms</span>
                    </div>
                    <div className="section-content">
                      <div className="form-group">
                        <label>Client Note</label>
                        <textarea
                          placeholder="Add a note for your client (visible on invoice)"
                          className={formErrors.clientNote ? 'input-error' : ''}
                          value={clientNote}
                          maxLength={CLIENT_NOTE_MAX}
                          onChange={(e) => {
                            setClientNote(stripEmoji(e.target.value));
                            if (formErrors.clientNote) {
                              setFormErrors((prev) => {
                                const { clientNote: _omit, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          rows={3}
                        />
                        {formErrors.clientNote ? (
                          <span className="inline-error">{formErrors.clientNote}</span>
                        ) : (
                          <span className="input-hint">
                            {clientNote.length}/{CLIENT_NOTE_MAX} characters
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Terms & Conditions</label>
                        <textarea
                          placeholder="Add terms and conditions"
                          className={formErrors.termsConditions ? 'input-error' : ''}
                          value={termsConditions}
                          maxLength={TERMS_MAX}
                          onChange={(e) => {
                            setTermsConditions(stripEmoji(e.target.value));
                            if (formErrors.termsConditions) {
                              setFormErrors((prev) => {
                                const { termsConditions: _omit, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          rows={3}
                        />
                        {formErrors.termsConditions ? (
                          <span className="inline-error">{formErrors.termsConditions}</span>
                        ) : (
                          <span className="input-hint">
                            {termsConditions.length}/{TERMS_MAX} characters
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {showPreview && (
                <div className="drawer-preview">
                  <div className="preview-header">
                    <div className="preview-tabs">
                      <button
                        className={`preview-tab ${previewMode === "Invoice PDF" ? "active" : ""
                          }`}
                        onClick={() => setPreviewMode("Invoice PDF")}
                      >
                        <FileText size={14} />
                        Invoice PDF
                      </button>
                      <button
                        className={`preview-tab ${previewMode === "Email" ? "active" : ""
                          }`}
                        onClick={() => setPreviewMode("Email")}
                      >
                        <Mail size={14} />
                        Email
                      </button>
                      <button
                        className={`preview-tab ${previewMode === "Payment" ? "active" : ""
                          }`}
                        onClick={() => setPreviewMode("Payment")}
                      >
                        Payment
                      </button>
                    </div>
                  </div>

                  <div className="preview-content">
                    {previewMode === "Payment" && (
                      <div className="payment-preview-card">
                        <div className="payment-card-header">
                          <span className="payment-brand">Maxelpay</span>
                          <span className="payment-secure">Secure Payment</span>
                        </div>

                        <div className="payment-amount-section">
                          <span className="payment-label">Amount Due</span>
                          <div className="payment-amount">
                            <span className="amount-value">
                              ${formatUSD(grandTotal)}
                            </span>
                            <span className="amount-token">USD</span>
                          </div>
                          <span className="payment-invoice-id">
                            Invoice: {nextInvoiceId}
                          </span>
                        </div>

                        <div className="qr-section">
                          <div className="qr-label">Scan to Pay</div>
                          <div className="qr-placeholder">
                            {/* Real QR uses the external api.qrserver.com image service (same as PaymentPage.jsx); makes an external call. */}
                            {previewLinkId ? (
                              <img
                                className="qr-image"
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`${window.location.origin}/invoice/${previewLinkId}`)}`}
                                alt="Invoice payment QR code"
                                width={150}
                                height={150}
                              />
                            ) : (
                              <span className="qr-pending-text">
                                QR appears after the invoice is created
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="address-section">
                          <span className="address-label">SEND TO ADDRESS</span>
                          <div className="address-box">
                            <span className="address-text">{mockAddress}</span>
                            <button className="testt"
                            >
                              {<Copy size={20} />}
                            </button>
                          </div>
                        </div>

                        <div className="timer-section">
                          <span className="timer-label">
                            Payment expires in
                          </span>
                          <span className="timer-value">14:59</span>
                        </div>

                        <div className="payment-status">
                          <span className="status-dot"></span>
                          Waiting for payment...
                        </div>

                        <div className="payment-footer">
                          Secured by Maxelpay
                        </div>
                      </div>
                    )}

                    {previewMode === "Invoice PDF" && (
                      <div className="pdf-preview-card">
                        <div className="pdf-header">
                          <div className="pdf-brand">
                            {/* <div className="pdf-logo">M</div>
                            <span className="pdf-company">MaxelPay</span> */}
                            <img src="/images/maxelpay-Logo.svg" alt="MaxelPay" class="invoice-lg"></img>
                          </div>
                          <div className="pdf-invoice-badge">INVOICE</div>
                        </div>

                        <div className="pdf-invoice-info">
                          <div className="pdf-invoice-number">
                            <span className="pdf-label">Invoice No.</span>
                            <span className="pdf-value">{nextInvoiceId}</span>
                          </div>
                          <div className="pdf-invoice-date">
                            <span className="pdf-label">Date</span>
                            <span className="pdf-value">
                              {new Date().toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </span>
                          </div>
                          <div className="pdf-invoice-due">
                            <span className="pdf-label">Due Date</span>
                            <span className="pdf-value">
                              {invoiceForm.dueDate
                                ? new Date(
                                  invoiceForm.dueDate
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                                : "—"}
                            </span>
                          </div>
                        </div>

                        <div className="pdf-parties">
                          <div className="pdf-party">
                            <span className="pdf-party-label">From</span>
                            <span className="pdf-party-name">
                              Your Business Name
                            </span>
                            <span className="pdf-party-detail">
                              your@email.com
                            </span>
                          </div>
                          <div className="pdf-party">
                            <span className="pdf-party-label">Bill To</span>
                            <span className="pdf-party-name">
                              {invoiceForm.customerName || "Customer Name"}
                            </span>
                            <span className="pdf-party-detail">
                              {invoiceForm.customerEmail ||
                                "customer@email.com"}
                            </span>
                          </div>
                        </div>

                        <div className="pdf-items">
                          <div className="pdf-items-header">
                            <span>Description</span>
                            <span>Qty</span>
                            <span>Price</span>
                            <span>Amount</span>
                          </div>
                          {lineItems.map((item, index) => (
                            <div key={item.id} className="pdf-item-row">
                              <span className="pdf-item-desc">
                                {item.description || `Item ${index + 1}`}
                              </span>
                              <span className="pdf-item-qty">
                                {item.quantity}
                              </span>
                              <span className="pdf-item-price">
                                {formatUSD(item.unitPrice)}
                              </span>
                              <span className="pdf-item-amount">
                                {formatUSD(item.amount)}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="pdf-total-section">
                          <div className="pdf-total-row">
                            <span>Subtotal</span>
                            <span>${formatUSD(subtotal)}</span>
                          </div>
                          {includeGst && gstValue && (
                            <div className="pdf-total-row">
                              <span>GST {gstType === "percentage" ? `(${gstValue}%)` : "(Fixed)"}</span>
                              <span>${formatUSD(gstAmount)}</span>
                            </div>
                          )}
                          <div className="pdf-total-row pdf-grand-total">
                            <span>Total Due</span>
                            <span>${formatUSD(grandTotal)}</span>
                          </div>
                        </div>

                        <div className="pdf-payment-details">
                          <span className="pdf-payment-title">
                            Payment Details
                          </span>
                          <div className="pdf-payment-row">
                            <span>Currency:</span>
                            <span>USD ($)</span>
                          </div>
                        </div>

                        <div className="pdf-footer">
                          <span>Thank you for your business!</span>
                          <span className="pdf-powered">
                            Powered by MaxelPay
                          </span>
                        </div>
                      </div>
                    )}

                    {previewMode === "Email" && (
                      <div className="email-preview-card">
                        <div className="email-browser-bar">
                          <div className="email-browser-dots">
                            <span className="dot red"></span>
                            <span className="dot yellow"></span>
                            <span className="dot green"></span>
                          </div>
                          <span className="email-browser-title">
                            Email Preview
                          </span>
                        </div>

                        <div className="email-container">
                          <div className="email-header-info">
                            <div className="email-field">
                              <span className="email-field-label">To:</span>
                              <span className="email-field-value">
                                {invoiceForm.customerEmail ||
                                  "customer@email.com"}
                              </span>
                            </div>
                            <div className="email-field">
                              <span className="email-field-label">
                                Subject:
                              </span>
                              <span className="email-field-value">
                                Invoice {nextInvoiceId} from Your Business
                              </span>
                            </div>
                          </div>

                          <div className="email-body">
                            <div className="email-logo-section">
                              <div className="email-logo">M</div>
                              <span className="email-brand">MaxelPay</span>
                            </div>

                            <div className="email-greeting">
                              Hi {invoiceForm.customerName || "Customer"},
                            </div>

                            <div className="email-message">
                              You have received a new invoice. Please find the
                              details below:
                            </div>

                            <div className="email-invoice-box">
                              <div className="email-invoice-header">
                                <span className="email-invoice-id">
                                  {nextInvoiceId}
                                </span>
                                <span className="email-invoice-status">
                                  Pending
                                </span>
                              </div>
                              <div className="email-invoice-amount">
                                <span className="email-amount-label">
                                  Amount Due
                                </span>
                                <span className="email-amount-value">
                                  ${formatUSD(grandTotal)} USD
                                </span>
                              </div>
                              <div className="email-invoice-details">
                                <div className="email-detail-row">
                                  <span>Due Date:</span>
                                  <span>
                                    {invoiceForm.dueDate
                                      ? new Date(
                                        invoiceForm.dueDate
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                      : "Not set"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <button className="email-pay-btn">
                              Pay Invoice
                            </button>

                            <div className="email-note">
                              If you have any questions about this invoice,
                              please contact us.
                            </div>

                            <div className="email-footer-text">
                              <span>Thank you for your business!</span>
                              <span className="email-company">
                                Your Business Name
                              </span>
                            </div>
                          </div>

                          <div className="email-footer-bar">
                            <span>Secured by MaxelPay</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showDeleteModal && (
        <>
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)} />
          <div className="modal-container">
            <div className="modal-header title-cancel">
              <div className="modal-icon danger">
                <AlertTriangle size={24} />
              </div>
              <h3>Cancel Invoice</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to cancel invoice <strong>{selectedInvoice?.id}</strong>?</p>
              <p className="modal-note">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-outline"
                onClick={() => setShowDeleteModal(false)}
                disabled={isUpdating}
              >
                Keep Invoice
              </button>
              <button
                className="btn-danger"
                onClick={handleDeleteInvoice}
                disabled={isUpdating}
              >
                {isUpdating ? "Cancelling..." : "Cancel Invoice"}
              </button>
            </div>
          </div>
        </>
      )}

      {showStatusModal && (
        <>
          <div className="modal-overlay" onClick={() => setShowStatusModal(false)} />
          <div className="modal-container">
            <div className="modal-header">
              <div className="modal-icon">
                <Edit3 size={24} />
              </div>
              <h3>Update Invoice Status</h3>
            </div>
            <div className="modal-body">
              <p>Change status for invoice <strong>{selectedInvoice?.id}</strong></p>
              <div className="form-group">
                <label>New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="modal-select"
                >
                  <option value="draft">Draft</option>
                  <option value="open">Open</option>
                  <option value="pending">Pending</option>
                  <option value="partially_paid">Partially Paid</option>
                  <option value="paid">Paid</option>
                  <option value="overpaid">Overpaid</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-outline"
                onClick={() => setShowStatusModal(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleUpdateStatus}
                disabled={isUpdating}
              >
                {isUpdating ? "Updating..." : "Update Status"}
              </button>
            </div>
          </div>
        </>
      )}

      {showDueDateModal && (
        <>
          <div className="modal-overlay" onClick={() => setShowDueDateModal(false)} />
          <div className="modal-container">
            <div className="modal-header">
              <div className="modal-icon">
                <Calendar size={24} />
              </div>
              <h3>Change Due Date</h3>
            </div>
            <div className="modal-body">
              <p>Update due date for invoice <strong>{selectedInvoice?.id}</strong></p>
              <div className="form-group">
                <label>New Due Date</label>
                <input
                  type="date"
                  value={newDueDate}
                  onChange={(e) => {
                    if (!acceptDueDate(e.target.value)) return;
                    setNewDueDate(e.target.value);
                  }}
                  min={getTodayDate()}
                  className="modal-input"
                  {...pickerOnlyProps}
                />
                <span className="input-hint">Due date must be today or a future date</span>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-outline"
                onClick={() => setShowDueDateModal(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleUpdateDueDate}
                disabled={isUpdating}
              >
                {isUpdating ? "Updating..." : "Update Due Date"}
              </button>
            </div>
          </div>
        </>
      )}


      <style>{`
       

        /* ---- Enterprise redesign additions ---- */
        /* Filter tabs, search, Quick Filters and Columns now share ONE row:
           the tabs sit on the left and these controls are pushed to the right
           inside FilterTabs' spacer slot. */
        .invoices-inline-controls {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        /* More breathing room around the single toolbar row so the table below
           sits cleanly. */
        .invoices-page--ent .filter-tabs { margin-bottom: 22px; row-gap: 12px; }

        /* Pro-table already collapses to cards on mobile via data-label, so the
           old standalone mobile-card block is hidden to avoid duplicate lists. */
        .invoices-page--ent .invoice-cards-mobile { display: none !important; }

        .invoices-pro-table { margin-bottom: 1rem; }

        .invoices-pro-table .pro-table th.sortable {
          cursor: pointer;
          user-select: none;
        }

        .invoices-pro-table .inv-id { font-weight: 600; font-family: var(--iv-sans);}

        /* Draft rows read slightly muted. */
        .invoices-pro-table tr.row-muted td { opacity: 0.62; }

        /* Overdue due-date treatment: red date + "Xd late" tag. */
        .invoices-pro-table .due-late { color: var(--err); font-weight: 500; }
        .invoices-pro-table .due-late__tag { font-weight: 600; }

        .invoices-pro-table .empty-table { padding: 0; }


        .invoices-pager-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 1.5rem;
        }
        .invoices-pager-row .pager { flex: 1 1 auto; }

        @media (max-width: 576px) {
          .pdf-parties{overflow-y:scroll;}
          .invoices-pager-row { flex-direction: column-reverse; align-items: stretch; }
          .invoices-pager-row .page-size-select { width: 100%; }
        }

         @media (max-width: 375px) {
            .email-header-info{overflow: scroll;}
         }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
        }

        .page-header h1 {
          margin: 0 0 0.25rem 0;
          font-size: 1.75rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .page-header p {
          margin: 0;
          color: var(--text-secondary);
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .stat-item {
          background: var(--bg-primary);
          padding: 1.25rem;
          border-radius: var(--r-md);
          border: 1px solid var(--border-color);
        }

        .stat-label {
          display: block;
          font-size: 0.875rem;
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .stat-value.positive { color: var(--ok); }
        .stat-value.negative { color: var(--err); }

        /* Table styles */
        .invoices-table-card {
          background: var(--bg-primary);
          border-radius: var(--r-md);
          border: 1px solid var(--border-color);
          // overflow: hidden;
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--border-color);
          gap: 1rem;
        }

        .table-search { flex: 1; max-width: 300px; }

        .search-input {
          width: 100%;
          padding: 0.625rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          font-size: 0.9rem;
          outline: none;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .search-input:focus { border-color: var(--accent-primary); }

        .filter-select {
          padding: 0.625rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          font-size: 0.9rem;
          background: var(--bg-primary);
          color: var(--text-primary);
          cursor: pointer;
        }

        // .table-container {
        //   overflow-x: scroll;
        //   overflow-y: visible;
        // }

        .invoices-data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .invoices-data-table th {
          text-align: left;
          padding: 0.875rem 1rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-color);
        }

        .invoices-data-table th.sortable {
          cursor: pointer;
          user-select: none;
        }

        .invoices-data-table th.sortable:hover { color: var(--accent-primary); }

        .invoices-data-table td {
          padding: 1rem;
          border-bottom: 1px solid var(--border-color);
          font-size: 0.9rem;
          color: var(--text-primary);
        }

        .clickable-row {
          cursor: pointer;
          transition: background-color 0.15s;
        }

        .clickable-row:hover { background-color: var(--bg-secondary); }

        .invoice-cell {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .invoice-icon { color: var(--accent-primary); }
        .invoice-id { font-weight: 600; color: var(--text-primary); }

        .customer-cell { display: flex; flex-direction: column; }
        .customer-name { font-weight: 500; color: var(--text-primary); }
        .customer-email { font-size: 0.8rem; color: var(--text-secondary); }

        .amount { font-weight: 600; color: var(--text-primary); }

        .status-badge {
          display: inline-flex;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-badge.paid { background: var(--ok-soft); color: var(--ok-ink); }
        .status-badge.pending { background: var(--warn-soft); color: var(--warn-ink); }
        .status-badge.overdue { background: var(--err-soft); color: var(--err-ink); }
        .status-badge.draft { background: var(--surface-2); color: var(--t-500); }
        .status-badge.cancelled { background: var(--err-soft); color: var(--err-ink); }
        .status-badge.overpaid { background: var(--info-soft); color: var(--info-ink); }
        .status-badge.partially-paid { background: var(--info-soft); color: var(--info-ink); }
        .status-badge.open { background: var(--warn-soft); color: var(--warn-ink); }
        .status-badge.unsupported-token { background: #572a22ff; color: #dfd5cdff; }
        .status-badge.refunded { background: var(--brand-100); color: var(--brand-600); }

        .action-buttons { display: flex; gap: 0.25rem; }

        .action-icon-btn {
          padding: 0.5rem;
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-radius: 6px;
        }

        .action-icon-btn:hover { background: var(--bg-secondary); color: var(--text-primary); }

        .empty-table {
          text-align: center;
          padding: 3rem !important;
          color: var(--text-muted);
        }

        .empty-table svg { margin-bottom: 0; opacity: 1; }
        .empty-table p { margin: 0 0 1rem 0; }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border-color);
          border-top-color: var(--accent-primary);
          border-radius: 50%;
          margin: 0 auto 1rem;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .table-footer {
          padding: 0.75rem 1.5rem;
          border-top: 1px solid var(--border-color);
          background: var(--bg-secondary);
        }

        .table-info { font-size: 0.875rem; color: var(--text-secondary); }

        /* Buttons */
        .btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          background: var(--brand-600);
          color: white;
          border: none;
          border-radius: var(--r-sm);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
        }

        .btn-primary svg {
          flex-shrink: 0;
          display: block;
        }

        .btn-primary:hover { background: var(--brand-700); }
        .btn-primary:disabled { background: var(--t-400); cursor: not-allowed; }
        .btn-primary.btn-sm { padding: 0.5rem 1rem; font-size: 0.85rem; }

        .btn-outline {
          padding: 0.625rem 1rem;
          background: var(--bg-primary);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          font-family: var(--iv-sans);
        }

        .btn-outline:hover { background: var(--bg-secondary); }

        .btn-text {
          padding: 0.5rem;
          background: transparent;
          border: none;
          color: var(--text-secondary);
          font-size: 0.875rem;
          cursor: pointer;
        }

        .btn-text:hover { color: var(--text-primary); }

        /* Drawer */
        .drawer-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
          animation: drawer-scrim-in 260ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;

          /* Blur what is behind the scrim so the drawer reads as the focused
             layer rather than just the brightest one. -webkit- prefix is still
             required for Safari.

             The drawer sits above this element (z-index 1000 vs 999) and is
             opaque, so the form itself is never softened.

             No keyframe change is needed for the fade -- the element's own
             opacity ramp composites its blurred backdrop along with it, so the
             blur eases in with the dim. */
          -webkit-backdrop-filter: blur(6px);
          backdrop-filter: blur(6px);
        }

        /* Progressive enhancement, not a swap: the 0.5 dim above stays as the
           fallback for browsers with no backdrop-filter, and is lightened only
           where the blur actually renders -- with both at full strength the
           page behind goes muddy and the blur stops reading as depth. */
        @supports (backdrop-filter: blur(6px)) or
          (-webkit-backdrop-filter: blur(6px)) {
          .drawer-overlay {
            background: rgba(15, 23, 42, 0.42);
          }
        }

        .drawer-overlay.is-closing {
          animation: drawer-scrim-out 220ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
          pointer-events: none;
        }


        .invoice-drawer {
          position: fixed;
          top: 0;
          right: 0;
          width: 900px;
          max-width: 100%;
          height: 100vh;
          background: var(--bg-primary);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
          animation: drawer-in 320ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
          /* Maximise toggle rides the same curve as the entrance. */
          transition: width 320ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1));
        }

        /* Leaving. pointer-events is dropped so a fast second click cannot land
           on a panel that is already on its way out. */
        .invoice-drawer.is-closing {
          animation: drawer-out 220ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
          pointer-events: none;
        }

        /* Must follow both animated rules above: a media query adds no
           specificity, so document order is the only thing that makes this
           win. */
        @media (prefers-reduced-motion: reduce) {
          .invoice-drawer,
          .invoice-drawer.is-closing,
          .drawer-overlay,
          .drawer-overlay.is-closing {
            animation: none;
          }
        }

        /* Shared drawer motion. Same --mx-ease-glide curve as the page-section
           transitions (defined on :root in PageTransition.css, which
           DashboardLayout imports, so it is always in scope here).

           This is a cross-fade, not a slide. The travel is 28px, not the full
           100% the panel used to cover: over 380ms that long a journey read as
           sluggish rather than smooth, and on a 900px panel it meant most of
           the screen was still moving after the eye had found the content.
           Opacity now does the work and the offset only hints at direction.

           Out is faster than in (220ms vs 320ms), for the same reason it is in
           PageTransition.css: the exit is dead time, so it should be brisk.

           Only opacity and transform animate, so nothing re-lays-out mid-fade,
           and both end on 'transform: none' rather than translate3d(0,0,0) so
           the compositing layer is released once the panel settles. */
        @keyframes drawer-in {
          from {
            opacity: 0;
            transform: translate3d(28px, 0, 0);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }

        @keyframes drawer-out {
          from {
            opacity: 1;
            transform: none;
          }
          to {
            opacity: 0;
            transform: translate3d(28px, 0, 0);
          }
        }

        /* The scrim used to appear at full strength in a single frame, which is
           what made the opening read as abrupt however smooth the panel was. */
        @keyframes drawer-scrim-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes drawer-scrim-out {
          from { opacity: 1; }
          to { opacity: 0; }
        }


        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        .invoice-drawer.drawer-maximized {
          width: 100%;
        }

        .btn-icon-outline {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem;
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          background: var(--bg-primary);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-icon-outline:hover {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
          background: rgba(124, 58, 237, 0.1);
        }

        .drawer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--border-color);
          background: var(--bg-primary);
        }

        .drawer-header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .close-btn {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-btn:hover { background: var(--bg-secondary); color: var(--text-primary); }

        .drawer-title-section {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .drawer-icon-wrapper {
          width: 36px;
          height: 36px;
          background: rgba(124, 58, 237, 0.1);
          border-radius: var(--r-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent-primary);
        }

        .drawer-title-section h2 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .invoice-id-text {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .drawer-header-right {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .drawer-content {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        /* Form Section */
        .drawer-form {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border-right: 1px solid var(--border-color);
        }

        .form-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 1.5rem;
        }

        .form-section {
          margin-bottom: 1.5rem;
        }

        .section-header {
          display: flex !important;
          align-items: center !important;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .section-number {
          width: 24px;
          height: 24px;
          background: var(--brand-600);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .section-title {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 1rem;
        }

        .section-content {
          padding-left: 2.25rem;
        }

        .customer-search-box {
          position: relative;
          margin-bottom: 1rem;
        }

        .customer-search-box .search-icon {
          position: absolute;
          left: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
        }

        .customer-search-input {
          width: 100%;
          padding: 0.75rem 0.875rem 0.75rem 2.5rem;
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          font-size: 0.9rem;
          outline: none;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .customer-search-input:focus { border-color: var(--accent-primary); }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .form-group input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          font-size: 0.9rem;
          outline: none;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .form-group input:focus {
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .form-group .inline-error {
          margin-top: 0.25rem;
        }

        .form-group select {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          font-size: 0.9rem;
          outline: none;
          background: var(--bg-primary);
          color: var(--text-primary);
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          padding-right: 2.5rem;
        }

        .form-group select:focus {
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .form-group select:disabled {
          background-color: var(--bg-secondary);
          color: var(--text-muted);
          cursor: not-allowed;
        }

        .form-group textarea {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          font-size: 0.9rem;
          outline: none;
          resize: vertical;
          font-family: inherit;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .form-group textarea:focus {
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        /* Line Items Table */
        .line-items-table {
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          overflow: hidden;
          margin-bottom: 1rem;
        }

        .line-items-header {
          display: grid;
          grid-template-columns: 1fr 70px 100px 90px 40px;
          gap: 0.5rem;
          padding: 0.75rem;
          background: var(--bg-secondary);
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
        }

        .line-item-row {
          display: grid;
          grid-template-columns: 1fr 70px 100px 90px 40px;
          gap: 0.5rem;
          padding: 0.75rem;
          border-top: 1px solid var(--border-color);
          align-items: center;
        }

        .line-item-row input {
          padding: 0.5rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 0.85rem;
          outline: none;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: var(--iv-sans);
        }

        .line-item-row input:focus {
          border-color: var(--accent-primary);
        }

        .line-item-row .col-description {
          min-width: 0;
        }

        .line-item-row .col-qty,
        .line-item-row .col-price {
          text-align: right;
        }

        .item-amount {
          font-weight: 600;
          color: var(--text-primary);
          text-align: right;
          overflow: scroll;
        }

        .remove-item-btn {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .remove-item-btn:hover:not(:disabled) {
          color: var(--err);
          background: rgba(239, 68, 68, 0.1);
        }

        .line-item-row-wrapper {
          display: flex;
          flex-direction: column;
        }

        .line-item-errors {
          display: flex;
          gap: 1rem;
          padding: 0.25rem 0.75rem 0.5rem;
          padding-left: calc(1fr + 0.75rem);
        }

        .inline-error {
          color: var(--err);
          font-size: 0.75rem;
          display: block;
        }

        .input-error {
          border-color: var(--err) !important;
          background-color: rgba(239, 68, 68, 0.05) !important;
        }

        .input-error:focus {
          border-color: var(--err) !important;
          box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.1);
        }

        .col-qty-wrapper,
        .col-price-wrapper {
          display: flex;
          flex-direction: column;
        }

        .remove-item-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .add-item-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          // width: 100%;
          background: transparent;
          border: none;
          // border-top: 1px dashed var(--border-color);
          color: var(--accent-primary);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
        }

        .add-item-btn:hover {
          background: var(--bg-secondary);
        }

        /* Invoice Totals */
        .invoice-totals {
          background: var(--bg-secondary);
          border-radius: var(--r-sm);
          padding: 1rem;
          overflow-x: scroll;
        }

        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          font-size: 0.9rem;
          color: var(--text-secondary);
        }

        .total-row.grand-total {
          border-top: 1px solid var(--border-color);
          margin-top: 0.5rem;
          padding-top: 0.75rem;
          font-weight: 700;
          font-size: 1rem;
          color: var(--text-primary);
        }

        /* GST Toggle */
        .gst-toggle {
          margin-bottom: 1rem;
        }

        .gst-checkbox-group {
          display: flex;
          flex-direction: column;
        }

        .gst-checkbox-group::before {
          content: ' ';
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
          visibility: hidden;
        }

     .gst-checkbox-label {
  display: flex !important;
  align-items: center;        /* vertical center */
  justify-content: flex-start; /* 👈 LEFT ALIGN */
  gap: 0.75rem;               /* spacing between checkbox & text */
  cursor: pointer;
  padding: 0.594rem 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: var(--r-sm);
  background: var(--bg-secondary);
  transition: all 0.15s;
  width: 100%;
  height: 42px;
  box-sizing: border-box;
}

.gst-checkbox-label:hover {
  border-color: var(--accent-primary);
  background: rgba(124, 58, 237, 0.1);
}

.gst-checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: var(--accent-primary);
  cursor: pointer;
  margin-right: 10px; /* important */
}

.gst-checkbox-label span {
  font-size: 0.9rem;
  color: var(--text-primary);
  line-height: 1; /* 👈 text vertical alignment fix */
}


        .gst-toggle-inline {
          display: flex;
          align-items: center;
          height: 100%;
          padding-top: 0.5rem;
        }

        .toggle-label {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
        }

        .toggle-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: var(--brand-600);
        }

        .toggle-text {
          font-size: 0.9rem;
          color: var(--text-primary);
        }

        .gst-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .gst-input-wrapper input {
          padding-right: 2.5rem;
        }

        .gst-suffix {
          position: absolute;
          right: 0.75rem;
          color: var(--text-secondary);
          font-size: 0.875rem;
          pointer-events: none;
        }

        /* Selection Grid */
        .selection-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
        }

        .token-grid {
          grid-template-columns: repeat(3, 1fr);
        }

        .grid-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 0.75rem;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          font-size: 0.8rem;
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.15s;
          text-align: left;
        }

        .grid-btn:hover {
          border-color: var(--accent-primary);
        }

        .grid-btn.selected {
          background: var(--accent-primary);
          border-color: var(--accent-primary);
          color: white;
        }

        .grid-btn-icon {
          width: 20px;
          height: 20px;
          background: var(--bg-tertiary);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--text-secondary);
        }

        .grid-btn.selected .grid-btn-icon {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }

        .grid-btn-text {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .token-btn {
          justify-content: flex-start;
        }

        .token-icon {
          width: 20px;
          height: 20px;
          background: var(--bg-tertiary);
          border-radius: 50%;
        }

        .token-btn.selected .token-icon {
          background: rgba(255, 255, 255, 0.2);
        }

        .loading-text, .helper-text {
          padding: 1rem;
          text-align: center;
          color: var(--text-muted);
          font-size: 0.875rem;
        }

        /* Amount Input */
        .amount-input-wrapper {
          position: relative;
        }

        .amount-input-wrapper input {
          padding-right: 4rem;
        }

        .amount-token-badge {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: var(--brand-600);
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: var(--r-sm);
          font-size: 0.75rem;
          font-weight: 500;
        }

        /* Date Input */
        .date-input-wrapper {
          position: relative;
        }

        .date-input-wrapper input {
          width: 100%;
          padding: 0.75rem 1.5rem 0.75rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: var(--r-sm);
          font-size: 0.9rem;
          outline: none;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .date-input-wrapper input:focus {
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .date-icon {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
          pointer-events: none;
        }

        /* Preview Section */
        .drawer-preview {
          width: 320px;
          background: var(--bg-secondary);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .preview-header {
          padding: 1rem;
          background: var(--bg-primary);
          border-bottom: 1px solid var(--border-color);
        }

        .preview-title {
          display: block;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.75rem;
        }

        .preview-tabs {
          display: flex;
          gap: 0.25rem;
        }

        .preview-tab {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.5rem 0.75rem;
          background: var(--bg-tertiary);
          border: none;
          border-radius: 6px;
          font-size: 0.8rem;
          color: var(--text-secondary);
          cursor: pointer;
          font-family: var(--iv-sans);
        }

        .preview-tab:hover { background: var(--border-color); }

        .preview-tab.active {
          background: var(--accent-primary);
          color: white;
        }

        .preview-content {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
          display: block;
        }

        /* Payment Preview Card */
        .payment-preview-card {
          background: var(--grad-primary);
          border-radius: var(--r-lg);
          padding: 1.25rem;
          color: white;
        }

        .payment-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.25rem;
        }

        .payment-brand {
          font-weight: 700;
          font-size: 1rem;
        }

        .payment-secure {
          background: rgba(255, 255, 255, 0.2);
          padding: 0.25rem 0.5rem;
          border-radius: var(--r-sm);
          font-size: 0.65rem;
        }

        .payment-amount-section {
          text-align: center;
          margin-bottom: 1rem;
        }

        .payment-label {
          display: block;
          font-size: 0.7rem;
          opacity: 0.8;
          margin-bottom: 0.25rem;
        }

        .payment-amount {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 0.5rem;
        }

        .amount-value {
          font-size: 2rem;
          font-weight: 700;
        }

        .amount-token {
          font-size: 1rem;
          opacity: 0.9;
        }

        .payment-invoice-id {
          display: block;
          font-size: 0.7rem;
          opacity: 0.7;
          margin-top: 0.25rem;
        }

        .payment-details-row {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .payment-detail {
          flex: 1;
        }

        .detail-label {
          display: block;
          font-size: 0.6rem;
          opacity: 0.7;
          margin-bottom: 0.25rem;
        }

        .detail-value {
          font-size: 0.75rem;
        }

        .detail-badge {
          display: inline-block;
          background: rgba(255, 255, 255, 0.15);
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
        }

        .qr-section {
          text-align: center;
          margin-bottom: 1rem;
        }

        .qr-label {
          font-size: 0.65rem;
          opacity: 0.7;
          margin-bottom: 0.5rem;
        }

        .qr-placeholder {
          width: 100px;
          height: 100px;
          background: white;
          border-radius: var(--r-sm);
          margin: 0 auto;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .qr-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          width: 100%;
          height: 100%;
        }

        .qr-cell {
          background: #e5e7eb;
          border-radius: 1px;
        }

        .qr-cell.filled {
          background: #1a1a2e;
        }

        .qr-placeholder .qr-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 4px;
        }

        .qr-pending-text {
          font-size: 0.55rem;
          line-height: 1.3;
          color: #6b7280;
          text-align: center;
          padding: 0.25rem;
        }

        .stats-error {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding: 0.625rem 0.875rem;
          border-radius: var(--r-sm);
          background: var(--err-soft);
          color: var(--err-ink);
          font-size: 0.85rem;
        }

        .stats-error .btn-toolbar {
          margin-left: auto;
        }

        .address-section {
          margin-bottom: 1rem;
        }

        .address-label {
          display: block;
          font-size: 0.6rem;
          opacity: 0.7;
          text-align: center;
          margin-bottom: 0.375rem;
        }

        .address-box {
          background: rgba(255, 255, 255, 0.1);
          border-radius: var(--r-sm);
          padding: 0.5rem;
          text-align: center;
        }

        .address-text {
          display: block;
          font-size: 0.65rem;
          font-family: monospace;
          opacity: 0.9;
          margin-bottom: 0.375rem;
          word-break: break-all;
        }

        .copy-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          padding: 0.375rem 0.5rem;
          border-radius: 4px;
          font-size: 0.65rem;
          cursor: pointer;
        }

        .copy-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .timer-section {
          text-align: center;
          margin-bottom: 0.5rem;
        }

        .timer-label {
          display: block;
          font-size: 0.65rem;
          color: #fbbf24;
        }

        .timer-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #fbbf24;
        }

        .payment-status {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          opacity: 0.9;
          margin-bottom: 0.75rem;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          background: #fbbf24;
          border-radius: 50%;
          animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .payment-footer {
          text-align: center;
          font-size: 0.6rem;
          opacity: 0.5;
        }

        /* PDF Preview Styles */
        .pdf-preview-card {
          background: white;
          border-radius: var(--r-sm);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          font-size: 0.75rem;
        }

        .pdf-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: var(--grad-primary);
          color: white;
        }

        .pdf-brand {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .pdf-brand img.invoice-lg {
            max-width: 100px;
        }
        .pdf-logo {
          width: 28px;
          height: 28px;
          background: white;
          color: var(--brand-600);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.875rem;
        }

        .pdf-company {
          font-weight: 700;
          font-size: 1rem;
        }

        .pdf-invoice-badge {
          background: rgba(255, 255, 255, 0.2);
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          font-weight: 600;
          font-size: 0.7rem;
          letter-spacing: 0.05em;
        }

        .pdf-invoice-info {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .pdf-invoice-number,
        .pdf-invoice-date,
        .pdf-invoice-due {
          text-align: center;
        }

        .pdf-label {
          display: block;
          font-size: 0.6rem;
          color: #6b7280;
          text-transform: uppercase;
          margin-bottom: 0.125rem;
        }

        .pdf-value {
          font-weight: 600;
          color: #1a1a2e;
          font-size: 0.7rem;
        }

        .pdf-parties {
          display: flex;
          padding: 1rem;
          gap: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .pdf-party {
          flex: 1;
        }
        .pdf-party-label {
          display: block;
          font-size: 0.6rem;
          color: #6b7280;
          text-transform: uppercase;
          margin-bottom: 0.25rem;
        }

        .pdf-party-name {
          display: block;
          font-weight: 600;
          color: #1a1a2e;
          font-size: 0.75rem;
        }

        .pdf-party-detail {
          display: block;
          color: #6b7280;
          font-size: 0.65rem;
        }

        .pdf-items {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .pdf-items-header {
          display: grid;
          grid-template-columns: 1fr 40px 50px 60px;
          gap: 0.25rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 0.5rem;
          font-size: 0.6rem;
          color: #6b7280;
          text-transform: uppercase;
          font-weight: 600;
        }

        .pdf-items-header span:not(:first-child) {
          text-align: right;
        }

        .pdf-item-row {
          display: grid;
          grid-template-columns: 1fr 40px 50px 60px;
          gap: 0.25rem;
          padding: 0.375rem 0;
        }

        .pdf-item-desc {
          color: #374151;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .pdf-item-qty,
        .pdf-item-price,
        .pdf-item-amount {
          text-align: right;
        }

        .pdf-item-amount {
          font-weight: 600;
          color: #1a1a2e;
        }

        .pdf-total-section {
          padding: 0.75rem 1rem;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .pdf-total-row {
          display: flex;
          justify-content: space-between;
          padding: 0.25rem 0;
          color: #6b7280;
        }

        .pdf-grand-total {
          border-top: 1px solid #e5e7eb;
          margin-top: 0.375rem;
          padding-top: 0.5rem;
          font-weight: 700;
          color: #1a1a2e;
          font-size: 0.85rem;
        }

        .pdf-payment-details {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .pdf-payment-title {
          display: block;
          font-size: 0.6rem;
          color: #6b7280;
          text-transform: uppercase;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .pdf-payment-row {
          display: flex;
          justify-content: space-between;
          padding: 0.125rem 0;
          color: #374151;
          font-size: 0.7rem;
        }

        .pdf-footer {
          padding: 0.75rem 1rem;
          text-align: center;
          background: #f9fafb;
        }

        .pdf-footer span {
          display: block;
          color: #6b7280;
          font-size: 0.65rem;
        }

        .pdf-powered {
          margin-top: 0.25rem;
          font-size: 0.6rem !important;
          color: #9ca3af !important;
        }

        /* Email Preview Styles */
        .email-preview-card {
          background: #e5e7eb;
          border-radius: var(--r-sm);
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .email-browser-bar {
          display: flex;
          align-items: center;
          padding: 0.5rem 0.75rem;
          background: #f3f4f6;
          border-bottom: 1px solid #e5e7eb;
        }

        .email-browser-dots {
          display: flex;
          gap: 0.375rem;
          margin-right: 0.75rem;
        }

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .dot.red { background: #ef4444; }
        .dot.yellow { background: #eab308; }
        .dot.green { background: #22c55e; }

        .email-browser-title {
          font-size: 0.65rem;
          color: #6b7280;
        }

        .email-container {
          background: white;
          margin: 0.5rem;
          border-radius: 6px;
          overflow: hidden;
        }

        .email-header-info {
          padding: 0.75rem;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .email-field {
          display: flex;
          gap: 0.5rem;
          font-size: 0.65rem;
          margin-bottom: 0.25rem;
        }

        .email-field:last-child {
          margin-bottom: 0;
        }

        .email-field-label {
          color: #6b7280;
          font-weight: 500;
        }

        .email-field-value {
          color: #374151;
        }

        .email-body {
          padding: 1rem;
        }
.testt{
  background: none !important;
  color: #fff !important;
   border:none !important;
}
        .email-logo-section {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .email-logo {
          width: 24px;
          height: 24px;
          background: var(--brand-600);
          color: white;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.75rem;
        }

        .email-brand {
          font-weight: 700;
          font-size: 0.875rem;
          color: #1a1a2e;
        }

        .email-greeting {
          font-size: 0.75rem;
          color: #1a1a2e;
          margin-bottom: 0.5rem;
        }

        .email-message {
          font-size: 0.7rem;
          color: #6b7280;
          margin-bottom: 1rem;
        }

        .email-invoice-box {
          background: #f9fafb;
          border-radius: var(--r-sm);
          padding: 0.75rem;
          margin-bottom: 1rem;
          border: 1px solid #e5e7eb;
        }

        .email-invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .email-invoice-id {
          font-weight: 600;
          font-size: 0.75rem;
          color: #1a1a2e;
        }

        .email-invoice-status {
          background: #fef3c7;
          color: #d97706;
          padding: 0.125rem 0.5rem;
          border-radius: 9999px;
          font-size: 0.6rem;
          font-weight: 500;
        }

        .email-invoice-amount {
          text-align: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 0.5rem;
        }

        .email-amount-label {
          display: block;
          font-size: 0.6rem;
          color: #6b7280;
          margin-bottom: 0.125rem;
        }

        .email-amount-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--brand-600);
        }

        .email-invoice-details {
          font-size: 0.65rem;
        }

        .email-detail-row {
          display: flex;
          justify-content: space-between;
          padding: 0.25rem 0;
          color: #6b7280;
        }

        .email-pay-btn {
          width: 100%;
          padding: 0.625rem;
          background: var(--brand-600);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 1rem;
        }

        .email-pay-btn:hover {
          background: var(--brand-700);
        }

        .email-note {
          font-size: 0.6rem;
          color: #9ca3af;
          text-align: center;
          margin-bottom: 1rem;
        }

        .email-footer-text {
          text-align: center;
          padding-top: 0.75rem;
          border-top: 1px solid #e5e7eb;
        }

        .email-footer-text span {
          display: block;
          font-size: 0.65rem;
          color: #6b7280;
        }

        .email-company {
          font-weight: 600;
          color: #1a1a2e !important;
          margin-top: 0.125rem;
        }

        .email-footer-bar {
          padding: 0.5rem;
          text-align: center;
          background: #f9fafb;
          font-size: 0.6rem;
          color: #9ca3af;
        }

        @media (max-width: 1024px) {
          .invoice-drawer {
            width: 100%;
          }

          .drawer-preview {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .stats-row {
            grid-template-columns: repeat(2, 1fr);
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .selection-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Dropdown Menu */
        .dropdown-container {
          position: relative;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: var(--paper);
          border: 1px solid var(--line);
          border-radius: var(--r-sm);
          box-shadow: var(--sh-card);
          min-width: 180px;
          z-index: 1000;
          padding: 0.5rem 0;
          animation: dropdownFadeIn 0.15s ease-out;
        }

        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
          padding: 0.625rem 1rem;
          background: transparent;
          border: none;
          font-size: 0.875rem;
          color: var(--t-700);
          cursor: pointer;
          text-align: left;
          transition: background 0.15s;
        }

        .dropdown-item:hover {
          background: var(--surface-2);
        }

        .dropdown-item.danger {
          color: var(--err-ink);
        }

        .dropdown-item.danger:hover {
          background: var(--err-soft);
        }

        .dropdown-divider {
          height: 1px;
          background: var(--line);
          margin: 0.5rem 0;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1000;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-container {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: var(--paper);
          border-radius: var(--r-lg);
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
          width: 100%;
          max-width: 420px;
          z-index: 1001;
          animation: modalSlideIn 0.2s ease-out;
        }

        @keyframes modalSlideIn {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }

        .modal-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          border-bottom: 1px solid var(--line);
        }

        .modal-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--r-md);
          background: var(--brand-100);
          color: var(--brand-600);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-icon.danger {
          background: var(--err-soft);
          color: var(--err-ink);
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--ink);
        }

        .modal-body {
          padding: 1.5rem;
        }

        .modal-body p {
          margin: 0 0 1rem 0;
          color: var(--t-700);
          font-size: 0.95rem;
        }

        .modal-body p:last-child {
          margin-bottom: 0;
        }

        .modal-note {
          color: var(--t-500) !important;
          font-size: 0.875rem !important;
        }

        .modal-body .form-group {
          margin-bottom: 0;
        }

        .modal-select,
        .modal-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--line);
          border-radius: var(--r-sm);
          font-size: 0.9rem;
          outline: none;
          background: var(--paper);
        }

        .modal-select:focus,
        .modal-input:focus {
          border-color: var(--brand-600);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          border-top: 1px solid var(--line);
          background: var(--surface-2);
          border-radius: 0 0 var(--r-lg) var(--r-lg);
        }

        .btn-danger {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          background: var(--err);
          color: white;
          border: none;
          border-radius: var(--r-sm);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          font-family: var(--iv-sans);
        }

        .btn-danger:hover {
          background: var(--err-ink);
        }

        .btn-danger:disabled,
        .btn-primary:disabled,
        .btn-outline:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Input Hint */
        .input-hint {
          display: block;
          margin-top: 0.375rem;
          font-size: 0.75rem;
          color: var(--t-500);
        }

.search-box {
  position: relative; /* 🔥 REQUIRED */
  display: flex;
  align-items: center;
}

.search-box input {
  width: 100%;
  padding: 8px 60px 8px 36px; /* space for search + clear icons */
}

.search-icon {
  position: absolute;
  left: 12px;
color: var(--text-secondary);
}

.search-clear {
  position: absolute;
  right: 12px; /* 🔥 inside input */
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
color: var(--text-secondary);
}

.search-clear:hover {
  color: var(--text-primary);
}

      


      `}</style>
    </div>
  );
};

export default Invoices;
